import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Layout from './Layout';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    ConfigProvider,
    DatePicker,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Progress,
    Segmented,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Timeline,
    Typography,
    message
} from 'antd';
import {
    AppstoreOutlined,
    BarChartOutlined,
    ClearOutlined,
    CloseOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FilterOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    PhoneOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    SoundOutlined,
    SyncOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    UserOutlined
} from '@ant-design/icons';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';
import './BitrixAnalyticsPage.css';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import ruRU from 'antd/locale/ru_RU';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);
dayjs.locale('ru');

const {RangePicker} = DatePicker;
const {Title, Text} = Typography;
const {TextArea} = Input;
const API = '/api/bitrix/analytics';

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const DEFAULT_PANE = {
    filterPx: 280,
    dealsPx: 420
};

const VIEW_STORAGE_KEY = 'bitrix-analytics-view-v2';

const readStoredView = () => {
    try {
        const raw =
            localStorage.getItem(VIEW_STORAGE_KEY) ||
            localStorage.getItem('bitrix-analytics-view');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

const clampPane = (value, min, max) =>
    Math.max(min, Math.min(max, Number(value) || min));

const buildInitialView = () => {
    const saved = readStoredView() || {};
    const dealsPxRaw =
        saved.paneLayout?.dealsPx ??
        saved.paneLayout?.statsPx ??
        DEFAULT_PANE.dealsPx;
    return {
        boardView:
            saved.boardView === 'kanban' || saved.boardView === 'cards'
                ? saved.boardView
                : 'cards',
        filterVisible: saved.filterVisible !== false,
        dealsVisible:
            saved.dealsVisible != null
                ? saved.dealsVisible !== false
                : true,
        paneLayout: {
            filterPx: clampPane(
                saved.paneLayout?.filterPx ?? DEFAULT_PANE.filterPx,
                220,
                420
            ),
            dealsPx: clampPane(dealsPxRaw, 300, 640)
        }
    };
};

const STAGE_ORDER = [
    'Входящие',
    'Пройден первичный отбор',
    'Проведено телефонное интервью',
    'Записан на онлайн/очное интервью',
    'Подтверждено',
    'Пройдено собеседование',
    'Записан на тестовый день',
    'Пройден тестовый день',
    'Стажируется',
    'Трудоустроен',
    'Выход'
];

const STAGE_SHORT = {
    'Входящие': 'Входящие',
    'Пройден первичный отбор': 'Отбор',
    'Проведено телефонное интервью': 'Тел. интервью',
    'Записан на онлайн/очное интервью': 'Запись на интервью',
    'Подтверждено': 'Подтверждено',
    'Пройдено собеседование': 'Собеседование',
    'Записан на тестовый день': 'Запись на тест',
    'Пройден тестовый день': 'Тест. день',
    'Стажируется': 'Стажировка',
    'Трудоустроен': 'Найм',
    'Выход': 'Выход'
};

const STAGE_META = {
    'Входящие': {tag: 'blue', color: '#1677ff'},
    'Пройден первичный отбор': {tag: 'cyan', color: '#13c2c2'},
    'Проведено телефонное интервью': {tag: 'geekblue', color: '#2f54eb'},
    'Записан на онлайн/очное интервью': {tag: 'purple', color: '#722ed1'},
    'Подтверждено': {tag: 'green', color: '#389e0d'},
    'Пройдено собеседование': {tag: 'lime', color: '#7cb305'},
    'Записан на тестовый день': {tag: 'orange', color: '#fa8c16'},
    'Пройден тестовый день': {tag: 'gold', color: '#d4b106'},
    'Стажируется': {tag: 'processing', color: '#1890ff'},
    'Трудоустроен': {tag: 'success', color: '#52c41a'},
    'Выход': {tag: 'error', color: '#ff4d4f'}
};

const SEMANTIC_META = {
    P: {name: 'Сделка в работе', color: '#1677ff', tag: 'processing'},
    S: {name: 'Трудоустроено', color: '#52c41a', tag: 'success'},
    F: {name: 'Выход', color: '#ff4d4f', tag: 'error'}
};

const CHART_COLORS = {
    work: '#1677ff',
    success: '#52c41a',
    failed: '#ff4d4f',
    conversion: '#722ed1',
    total: '#13c2c2'
};

const truncateLabel = (value, max = 18) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1)}…`;
};

const DATE_PRESETS = [
    {label: 'Сегодня', value: [dayjs().startOf('day'), dayjs().endOf('day')]},
    {label: 'Неделя', value: [dayjs().startOf('isoWeek'), dayjs().endOf('isoWeek')]},
    {label: 'Месяц', value: [dayjs().startOf('month'), dayjs().endOf('month')]},
    {label: 'Квартал', value: [dayjs().startOf('quarter'), dayjs().endOf('quarter')]},
    {label: 'Год', value: [dayjs().startOf('year'), dayjs().endOf('year')]}
];

const toArray = value => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.result)) return value.result;
    return [];
};

const toNumber = value => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const getSemanticId = deal => {
    if (deal?.stage_semantic_id) return deal.stage_semantic_id;
    if (deal?.stage_name === 'Трудоустроен') return 'S';
    if (deal?.stage_name === 'Выход') return 'F';
    return 'P';
};

const formatDate = value => {
    if (!value) return '—';
    const date = dayjs(value);
    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : String(value);
};

const onlyPhoneDigits = value => String(value || '').replace(/\D+/g, '');

const phoneVariants = value => {
    const digits = onlyPhoneDigits(value);
    if (!digits) return [];
    const set = new Set([digits]);
    if (digits.length === 11 && digits.startsWith('8')) set.add(`7${digits.slice(1)}`);
    if (digits.length === 11 && digits.startsWith('7')) {
        set.add(`8${digits.slice(1)}`);
        set.add(digits.slice(1));
    }
    if (digits.length === 10) {
        set.add(`7${digits}`);
        set.add(`8${digits}`);
    }
    return [...set];
};

const phonesOverlap = (a, b) => {
    const left = phoneVariants(a);
    const right = new Set(phoneVariants(b));
    return left.some(item => right.has(item));
};

const phoneIndexKey = value => {
    const digits = onlyPhoneDigits(value);
    if (!digits) return '';
    return digits.length >= 10 ? digits.slice(-10) : digits;
};

const formatDurationSec = value => {
    const total = Math.max(0, Math.floor(toNumber(value)));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const formatMoney = value => {
    if (value == null || value === '') return '—';
    return `${Number(value).toLocaleString('ru-RU', {
        maximumFractionDigits: 2
    })} ₽`;
};

const sortStages = (a, b) => {
    const first = STAGE_ORDER.indexOf(a.stage_name || a.name);
    const second = STAGE_ORDER.indexOf(b.stage_name || b.name);
    return (first === -1 ? 999 : first) - (second === -1 ? 999 : second);
};

const SummaryKpiGrid = ({ summary, funnelStageHint = false }) => (
    <div className="ba-stats__kpi ba-stats__kpi--wide">
        <div className="ba-stats__kpi-item ba-stats__kpi-item--primary ba-stats__kpi-item--total">
            <span className="ba-stats__kpi-label">Всего</span>
            <span className="ba-stats__kpi-value">{summary.total}</span>
        </div>
        <div className="ba-stats__kpi-item ba-stats__kpi-item--primary ba-stats__kpi-item--work">
            <span className="ba-stats__kpi-label">В работе</span>
            <span className="ba-stats__kpi-value" style={{color: '#1677ff'}}>
                {summary.work}
            </span>
        </div>
        <div className="ba-stats__kpi-item ba-stats__kpi-item--primary ba-stats__kpi-item--success">
            <span className="ba-stats__kpi-label">
                Найм
                {funnelStageHint ? (
                    <Text type="secondary" style={{fontSize: 10, display: 'block'}}>
                        доходили до стадии
                    </Text>
                ) : null}
            </span>
            <span className="ba-stats__kpi-value" style={{color: '#52c41a'}}>
                {summary.success}
            </span>
        </div>
        <div className="ba-stats__kpi-item ba-stats__kpi-item--primary ba-stats__kpi-item--failed">
            <span className="ba-stats__kpi-label">
                Выход
                {funnelStageHint ? (
                    <Text type="secondary" style={{fontSize: 10, display: 'block'}}>
                        доходили до стадии
                    </Text>
                ) : null}
            </span>
            <span className="ba-stats__kpi-value" style={{color: '#ff4d4f'}}>
                {summary.failed}
            </span>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Конверсия</span>
            <span className="ba-stats__kpi-value" style={{color: '#722ed1'}}>
                {summary.conversion}%
            </span>
            <Progress
                percent={Math.min(100, summary.conversion)}
                showInfo={false}
                size="small"
                strokeColor="#722ed1"
                style={{marginTop: 4}}
            />
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Доля найма</span>
            <span className="ba-stats__kpi-value">{summary.hire_rate}%</span>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Выход %</span>
            <span className="ba-stats__kpi-value">{summary.exit_rate}%</span>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Звонки</span>
            <span className="ba-stats__kpi-value">{summary.calls_count}</span>
            <Text type="secondary" style={{fontSize: 11}}>
                запись: {summary.recorded_count}
            </Text>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Разговоры</span>
            <span className="ba-stats__kpi-value" style={{fontSize: 18}}>
                {formatDurationSec(summary.talk_duration_sec)}
            </span>
            <Text type="secondary" style={{fontSize: 11}}>
                ср. {formatDurationSec(summary.avg_duration_sec)}
            </Text>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">Перемещения</span>
            <span className="ba-stats__kpi-value">{summary.movements_count}</span>
        </div>
        <div className="ba-stats__kpi-item">
            <span className="ba-stats__kpi-label">&gt;14 дн.</span>
            <span className="ba-stats__kpi-value" style={{color: '#fa8c16'}}>
                {summary.stale_work}
            </span>
        </div>
    </div>
);

const BitrixAnalyticsPage = () => {
    const [editForm] = Form.useForm();
    const initialViewRef = useRef(null);
    if (!initialViewRef.current) {
        initialViewRef.current = buildInitialView();
    }
    const initialView = initialViewRef.current;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [boardView, setBoardView] = useState(initialView.boardView);
    const [filterVisible, setFilterVisible] = useState(
        initialView.filterVisible
    );
    const [dealsVisible, setDealsVisible] = useState(
        initialView.dealsVisible
    );
    const [paneLayout, setPaneLayout] = useState({
        ...initialView.paneLayout
    });
    const paneLayoutRef = useRef({...initialView.paneLayout});
    const filterVisibleRef = useRef(initialView.filterVisible);
    const dealsVisibleRef = useRef(initialView.dealsVisible);
    const boardViewRef = useRef(initialView.boardView);
    const loadGenRef = useRef(0);

    const [dealOpen, setDealOpen] = useState(false);
    const [dealLoading, setDealLoading] = useState(false);
    const [savingDeal, setSavingDeal] = useState(false);
    const [currentDeal, setCurrentDeal] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);
    const [dealCalls, setDealCalls] = useState([]);
    const [dealCallsLoading, setDealCallsLoading] = useState(false);
    const [dealCallsError, setDealCallsError] = useState('');
    const [hrCalls, setHrCalls] = useState([]);
    const [hrCallsLoading, setHrCallsLoading] = useState(false);
    const [hrCallsError, setHrCallsError] = useState('');
    const [audioUrls, setAudioUrls] = useState({});
    const [audioLoadingName, setAudioLoadingName] = useState('');
    const [activeAudioName, setActiveAudioName] = useState('');
    const audioObjectUrlRef = useRef(null);
    const [recruiterStats, setRecruiterStats] = useState([]);
    const [selectedHrId, setSelectedHrId] = useState(null);
    const [hrSheetOpen, setHrSheetOpen] = useState(false);
    const [vacancyPanelOpen, setVacancyPanelOpen] = useState(false);
    const [vacancyDirectory, setVacancyDirectory] = useState([]);
    const [vacancyDirectoryLoading, setVacancyDirectoryLoading] = useState(false);
    const [vacancyFunnelByName, setVacancyFunnelByName] = useState({});
    const [funnelTotals, setFunnelTotals] = useState(null);
    const [vacancyConversion, setVacancyConversion] = useState([]);
    const [staleDeals, setStaleDeals] = useState([]);
    const [vacancySyncing, setVacancySyncing] = useState(false);
    const [vacancyCreateOpen, setVacancyCreateOpen] = useState(false);
    const [vacancyCreateName, setVacancyCreateName] = useState('');
    const [vacancyCreateActive, setVacancyCreateActive] = useState(true);
    const [vacancyCreating, setVacancyCreating] = useState(false);
    const [selectedVacancyId, setSelectedVacancyId] = useState(null);
    const [vacancyDetail, setVacancyDetail] = useState(null);
    const [vacancyDetailLoading, setVacancyDetailLoading] = useState(false);
    const [vacancyExpenses, setVacancyExpenses] = useState([]);
    const [expenseSaving, setExpenseSaving] = useState(false);
    const [expenseForm, setExpenseForm] = useState({
        category: '',
        amount: null,
        expense_date: dayjs().format('YYYY-MM-DD'),
        comment: ''
    });

    const [summary, setSummary] = useState({
        stale_work: 0,
        movements_count: 0
    });
    const [stageOptions, setStageOptions] = useState([]);
    const [deals, setDeals] = useState([]);
    const [users, setUsers] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [search, setSearch] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [vacancyFilter, setVacancyFilter] = useState('');
    const [vacancyOptions, setVacancyOptions] = useState([]);
    const [hasRecordingOnly, setHasRecordingOnly] = useState(false);
    const [filterPresets, setFilterPresets] = useState([]);
    const [selectedPresetId, setSelectedPresetId] = useState(undefined);
    const [presetSaving, setPresetSaving] = useState(false);
    const [presetModalOpen, setPresetModalOpen] = useState(false);
    const [presetName, setPresetName] = useState('');
    const [presetGlobal, setPresetGlobal] = useState(false);
    const [filters, setFilters] = useState({
        dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
        dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
        modifiedDateFrom: null,
        modifiedDateTo: null,
        stage: undefined,
        semantic: undefined,
        user: undefined,
        source: undefined
    });

    const currentRoleId = useMemo(() => {
        try {
            const raw = localStorage.getItem('user');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return Number(
                parsed?.role_id ?? parsed?.roleId ?? localStorage.getItem('roleId')
            );
        } catch {
            return Number(localStorage.getItem('roleId')) || null;
        }
    }, []);

    const canSaveGlobalPreset =
        currentRoleId === 1 || currentRoleId === 8;

    const [analyticsScope, setAnalyticsScope] = useState({
        mode: 'all',
        bitrix_user_id: null,
        can_manage_vacancies: true,
        can_save_global_filters: canSaveGlobalPreset,
        is_self_only: false,
        linked: true
    });

    const isSelfOnlyHr = Boolean(analyticsScope.is_self_only);
    const selfBitrixUserId = analyticsScope.bitrix_user_id
        ? String(analyticsScope.bitrix_user_id)
        : null;

    const requestConfig = useMemo(() => {
        const token = localStorage.getItem('token');
        return {
            headers: token ? {Authorization: `Bearer ${token}`} : {}
        };
    }, []);

    const buildParams = useCallback(() => {
        const params = new URLSearchParams();
        if (filters.dateFrom) params.append('dateFrom', `${filters.dateFrom} 00:00:00`);
        if (filters.dateTo) params.append('dateTo', `${filters.dateTo} 23:59:59`);
        if (filters.modifiedDateFrom) {
            params.append('modifiedDateFrom', `${filters.modifiedDateFrom} 00:00:00`);
        }
        if (filters.modifiedDateTo) {
            params.append('modifiedDateTo', `${filters.modifiedDateTo} 23:59:59`);
        }
        if (filters.stage) params.append('stage', filters.stage);
        if (filters.semantic) params.append('semantic', filters.semantic);
        if (filters.user) params.append('user', filters.user);
        if (filters.source) params.append('source', filters.source);
        if (vacancyFilter.trim()) params.append('vacancy', vacancyFilter.trim());
        if (hasRecordingOnly) params.append('hasRecording', '1');
        return params.toString();
    }, [filters, vacancyFilter, hasRecordingOnly]);

    const loadData = useCallback(async () => {
        const gen = ++loadGenRef.current;
        setLoading(true);
        setError('');
        setVacancyFunnelByName({});
        setFunnelTotals(null);
        setVacancyConversion([]);
        setStaleDeals([]);
        try {
            const query = buildParams();
            const suffix = query ? `?${query}` : '';
            const join = query ? '&' : '?';

            const [summaryResult, dealsResult] = await Promise.allSettled([
                axios.get(`${API}/summary${suffix}`, requestConfig),
                axios.get(`${API}/deals${suffix}`, requestConfig)
            ]);

            if (gen !== loadGenRef.current) return;

            const summaryData =
                summaryResult.status === 'fulfilled'
                    ? summaryResult.value.data || {}
                    : {};
            const dealData = toArray(
                dealsResult.status === 'fulfilled'
                    ? dealsResult.value.data
                    : []
            ).map(item => ({
                ...item,
                stage_semantic_id: getSemanticId(item)
            }));

            setSummary({
                stale_work: toNumber(summaryData.stale_work),
                movements_count: toNumber(summaryData.movements_count)
            });
            setDeals(dealData);

            // Опции фильтров сразу из сделок — без ожидания лишних API
            const usersFromDeals = [];
            const userSeen = new Set();
            const stageFromDeals = [];
            const stageSeen = new Set();
            const vacancyFromDeals = [];
            const vacancySeen = new Set();

            dealData.forEach(deal => {
                const uid = String(deal.assigned_by_id || '');
                if (uid && !userSeen.has(uid)) {
                    userSeen.add(uid);
                    usersFromDeals.push({
                        id: uid,
                        name: deal.user_name || `Пользователь ${uid}`
                    });
                }
                if (deal.stage_id && !stageSeen.has(String(deal.stage_id))) {
                    stageSeen.add(String(deal.stage_id));
                    stageFromDeals.push({
                        stage_id: deal.stage_id,
                        stage_name: deal.stage_name || deal.stage_id
                    });
                }
                const vac = String(deal.vacancy_name || '').trim();
                if (vac && !vacancySeen.has(vac)) {
                    vacancySeen.add(vac);
                    vacancyFromDeals.push({value: vac, label: vac});
                }
            });

            if (usersFromDeals.length) {
                setUsers(
                    usersFromDeals.sort((a, b) =>
                        a.name.localeCompare(b.name, 'ru')
                    )
                );
            }
            if (stageFromDeals.length) {
                setStageOptions(stageFromDeals.sort(sortStages));
            }
            if (vacancyFromDeals.length) {
                setVacancyOptions(
                    vacancyFromDeals.sort((a, b) =>
                        a.label.localeCompare(b.label, 'ru')
                    )
                );
            }

            setLoading(false);

            if (
                summaryResult.status === 'rejected' ||
                dealsResult.status === 'rejected'
            ) {
                setError('Часть данных не загружена');
            }

            // Фон: справочники, звонки ATS, by-recruiter, воронка по вакансиям
            Promise.allSettled([
                axios.get(`${API}/users`, requestConfig),
                axios.get(`${API}/stages`, requestConfig),
                axios.get(`${API}/filter-sources`, requestConfig),
                axios.get(`${API}/vacancies`, requestConfig),
                hasRecordingOnly
                    ? Promise.resolve({data: dealData})
                    : axios.get(
                          `${API}/deals${suffix}${join}withCalls=1`,
                          requestConfig
                      ),
                axios.get(`${API}/by-recruiter${suffix}`, requestConfig),
                axios.get(`${API}/vacancy-funnel${suffix}`, requestConfig),
                axios.get(`${API}/vacancies/conversion${suffix}`, requestConfig),
                axios.get(`${API}/stale-deals${suffix}${join}days=3`, requestConfig)
            ]).then(results => {
                if (gen !== loadGenRef.current) return;

                const ok = (index, fallback) => {
                    const row = results[index];
                    return row.status === 'fulfilled'
                        ? row.value.data
                        : fallback;
                };

                const userData = toArray(ok(0, []))
                    .filter(item => item?.id)
                    .reduce((acc, item) => {
                        const id = String(item.id);
                        if (acc.some(u => u.id === id)) return acc;
                        acc.push({
                            id,
                            name: item.name || `Пользователь ${item.id}`
                        });
                        return acc;
                    }, []);
                if (userData.length) setUsers(userData);

                const allStageData = toArray(ok(1, []))
                    .filter(item => item?.stage_id)
                    .sort(sortStages);
                if (allStageData.length) setStageOptions(allStageData);

                setSourceOptions(toArray(ok(2, [])));

                const vacOpts = toArray(ok(3, [])).map(item => ({
                    value: item.value || item.label || item,
                    label: item.label || item.value || item
                }));
                if (vacOpts.length) setVacancyOptions(vacOpts);

                const dealsWithCalls = toArray(ok(4, [])).map(item => ({
                    ...item,
                    stage_semantic_id: getSemanticId(item)
                }));
                if (dealsWithCalls.length) {
                    setDeals(dealsWithCalls);
                }

                setRecruiterStats(
                    toArray(ok(5, [])).map(item => ({
                        ...item,
                        assigned_by_id: String(item.assigned_by_id || '0'),
                        calls_count: toNumber(item.calls_count),
                        movements_count: toNumber(item.movements_count),
                        exit_rate: toNumber(item.exit_rate),
                        recorded_count: toNumber(item.recorded_count),
                        talk_duration_sec: toNumber(item.talk_duration_sec)
                    }))
                );

                const funnelPayload = ok(6, null);
                const funnelList = Array.isArray(funnelPayload)
                    ? funnelPayload
                    : toArray(funnelPayload?.vacancies);
                const funnelMap = {};
                funnelList.forEach(item => {
                    const name = String(item.vacancy_name || '').trim();
                    if (!name) return;
                    const stages = {};
                    toArray(item.stages).forEach(stage => {
                        const stageName = String(
                            stage.stage_name || stage.name || ''
                        ).trim();
                        if (!stageName) return;
                        stages[stageName] = toNumber(stage.count);
                    });
                    funnelMap[name] = {
                        stages,
                        incoming: toNumber(item.incoming || stages['Входящие'])
                    };
                });
                setVacancyFunnelByName(funnelMap);

                const totalsRaw = funnelPayload?.totals || null;
                if (totalsRaw && typeof totalsRaw === 'object') {
                    const stages = {};
                    if (totalsRaw.stages && typeof totalsRaw.stages === 'object') {
                        Object.entries(totalsRaw.stages).forEach(([key, val]) => {
                            stages[key] = toNumber(val);
                        });
                    }
                    setFunnelTotals({
                        stages,
                        incoming: toNumber(
                            totalsRaw.incoming ?? stages['Входящие']
                        ),
                        hire: toNumber(
                            totalsRaw.hire ?? stages['Трудоустроен']
                        ),
                        exit: toNumber(totalsRaw.exit ?? stages['Выход'])
                    });
                } else {
                    setFunnelTotals(null);
                }

                setVacancyConversion(
                    toArray(ok(7, [])).map(item => ({
                        ...item,
                        id: item.id,
                        name: item.name || '—',
                        responses: toNumber(item.responses),
                        success: toNumber(item.success),
                        failed: toNumber(item.failed),
                        spend: toNumber(item.spend),
                        avg_cost_per_response:
                            item.avg_cost_per_response == null
                                ? null
                                : toNumber(item.avg_cost_per_response),
                        conversion: toNumber(item.conversion),
                        by_day: toArray(item.by_day).map(day => ({
                            ...day,
                            day: day.day,
                            responses: toNumber(day.responses),
                            spend: toNumber(day.spend),
                            avg_cost_per_response:
                                day.avg_cost_per_response == null
                                    ? null
                                    : toNumber(day.avg_cost_per_response)
                        }))
                    }))
                );

                const stalePayload = ok(8, {deals: []});
                setStaleDeals(
                    toArray(stalePayload?.deals).map(item => ({
                        ...item,
                        idle_days: toNumber(item.idle_days)
                    }))
                );
            });
        } catch (loadError) {
            if (gen !== loadGenRef.current) return;
            const text =
                loadError.response?.data?.message ||
                loadError.response?.data?.error ||
                loadError.message ||
                'Не удалось загрузить данные HR-аналитики';
            setError(text);
            message.error(text);
            setLoading(false);
        }
    }, [requestConfig, buildParams, hasRecordingOnly]);

    const loadVacancyDirectory = useCallback(async () => {
        setVacancyDirectoryLoading(true);
        try {
            const query = buildParams();
            const suffix = query
                ? `?withStats=true&${query}`
                : '?withStats=true';
            const {data} = await axios.get(
                `${API}/vacancies/directory${suffix}`,
                requestConfig
            );
            setVacancyDirectory(toArray(data));
        } catch (err) {
            message.error(
                err.response?.data?.error ||
                    'Не удалось загрузить справочник вакансий'
            );
        } finally {
            setVacancyDirectoryLoading(false);
        }
    }, [requestConfig, buildParams]);

    const openVacancyPanel = useCallback(() => {
        setVacancyPanelOpen(true);
        setSelectedVacancyId(null);
        setVacancyDetail(null);
        loadVacancyDirectory();
    }, [loadVacancyDirectory]);

    const syncVacancies = useCallback(async () => {
        setVacancySyncing(true);
        try {
            const {data} = await axios.post(
                `${API}/vacancies/sync`,
                {},
                requestConfig
            );
            message.success(
                `Синхронизация: +${data.created || 0}, обновлено ${data.updated || 0}`
            );
            await loadVacancyDirectory();
            await loadData();
        } catch (err) {
            message.error(
                err.response?.data?.error || 'Ошибка синхронизации вакансий'
            );
        } finally {
            setVacancySyncing(false);
        }
    }, [requestConfig, loadVacancyDirectory, loadData]);

    const toggleVacancyActive = useCallback(
        async (id, is_active) => {
            try {
                await axios.patch(
                    `${API}/vacancies/${id}`,
                    {is_active},
                    requestConfig
                );
                setVacancyDirectory(prev =>
                    prev.map(item =>
                        item.id === id ? {...item, is_active} : item
                    )
                );
                await loadData();
            } catch (err) {
                message.error(
                    err.response?.data?.error || 'Не удалось обновить вакансию'
                );
            }
        },
        [requestConfig, loadData]
    );

    const loadVacancyDetail = useCallback(
        async vacancyId => {
            setSelectedVacancyId(vacancyId);
            setVacancyDetailLoading(true);
            setExpenseForm({
                category: '',
                amount: null,
                expense_date: dayjs().format('YYYY-MM-DD'),
                comment: ''
            });
            try {
                const query = buildParams();
                const suffix = query ? `?${query}` : '';
                const [statsRes, expensesRes] = await Promise.all([
                    axios.get(
                        `${API}/vacancies/${vacancyId}/stats${suffix}`,
                        requestConfig
                    ),
                    axios.get(
                        `${API}/vacancies/${vacancyId}/expenses`,
                        requestConfig
                    )
                ]);
                setVacancyDetail(statsRes.data);
                setVacancyExpenses(toArray(expensesRes.data));
            } catch (err) {
                message.error(
                    err.response?.data?.error ||
                        'Не удалось загрузить статистику вакансии'
                );
                setVacancyDetail(null);
            } finally {
                setVacancyDetailLoading(false);
            }
        },
        [requestConfig, buildParams]
    );

    const createVacancy = useCallback(async () => {
        const name = String(vacancyCreateName || '')
            .replace(/\s+/g, ' ')
            .trim();
        if (!name) {
            message.warning('Укажите название вакансии');
            return;
        }
        setVacancyCreating(true);
        try {
            const {data} = await axios.post(
                `${API}/vacancies`,
                {
                    name,
                    is_active: vacancyCreateActive
                },
                requestConfig
            );
            message.success(`Вакансия «${data.name}» создана`);
            setVacancyCreateOpen(false);
            setVacancyCreateName('');
            setVacancyCreateActive(true);
            await loadVacancyDirectory();
            await loadData();
            if (data?.id) {
                await loadVacancyDetail(data.id);
            }
        } catch (err) {
            message.error(
                err.response?.data?.error || 'Не удалось создать вакансию'
            );
        } finally {
            setVacancyCreating(false);
        }
    }, [
        vacancyCreateName,
        vacancyCreateActive,
        requestConfig,
        loadVacancyDirectory,
        loadData,
        loadVacancyDetail
    ]);

    const openVacancyByName = useCallback(
        async name => {
            const needle = String(name || '').trim();
            if (!needle) return;
            openVacancyPanel();
            try {
                let list = vacancyDirectory;
                if (!list.length) {
                    const {data} = await axios.get(
                        `${API}/vacancies/directory`,
                        requestConfig
                    );
                    list = toArray(data);
                    setVacancyDirectory(list);
                }
                const found = list.find(
                    item =>
                        String(item.name || '').trim() === needle ||
                        String(item.name || '')
                            .toLowerCase()
                            .includes(needle.toLowerCase())
                );
                if (found?.id) {
                    await loadVacancyDetail(found.id);
                } else {
                    message.info(
                        'Вакансия не найдена в справочнике — выполните синхронизацию'
                    );
                }
            } catch (err) {
                message.error(
                    err.response?.data?.error ||
                        'Не удалось открыть вакансию'
                );
            }
        },
        [
            openVacancyPanel,
            vacancyDirectory,
            requestConfig,
            loadVacancyDetail
        ]
    );

    const saveExpense = useCallback(async () => {
        if (!selectedVacancyId) return;
        if (
            expenseForm.amount == null ||
            !Number.isFinite(Number(expenseForm.amount))
        ) {
            message.warning('Укажите сумму');
            return;
        }
        setExpenseSaving(true);
        try {
            await axios.post(
                `${API}/vacancies/${selectedVacancyId}/expenses`,
                {
                    category: expenseForm.category,
                    amount: Number(expenseForm.amount),
                    expense_date: expenseForm.expense_date || null,
                    comment: expenseForm.comment || null
                },
                requestConfig
            );
            message.success('Расход добавлен');
            await loadVacancyDetail(selectedVacancyId);
            loadData();
        } catch (err) {
            message.error(
                err.response?.data?.error || 'Не удалось сохранить расход'
            );
        } finally {
            setExpenseSaving(false);
        }
    }, [
        selectedVacancyId,
        expenseForm,
        requestConfig,
        loadVacancyDetail,
        loadData
    ]);

    const deleteExpense = useCallback(
        async expenseId => {
            if (!selectedVacancyId) return;
            try {
                await axios.delete(
                    `${API}/vacancies/${selectedVacancyId}/expenses/${expenseId}`,
                    requestConfig
                );
                message.success('Расход удалён');
                await loadVacancyDetail(selectedVacancyId);
                loadData();
            } catch (err) {
                message.error(
                    err.response?.data?.error || 'Не удалось удалить расход'
                );
            }
        },
        [selectedVacancyId, requestConfig, loadVacancyDetail, loadData]
    );

    const loadFilterPresets = useCallback(async () => {
        try {
            const {data} = await axios.get(
                `${API}/filter-presets`,
                requestConfig
            );
            setFilterPresets(toArray(data));
        } catch (err) {
            console.warn('filter presets load', err);
        }
    }, [requestConfig]);

    const loadAnalyticsScope = useCallback(async () => {
        try {
            const {data} = await axios.get(`${API}/scope`, requestConfig);
            setAnalyticsScope({
                mode: data?.mode === 'self' ? 'self' : 'all',
                bitrix_user_id: data?.bitrix_user_id || null,
                can_manage_vacancies: Boolean(data?.can_manage_vacancies),
                can_save_global_filters: Boolean(
                    data?.can_save_global_filters
                ),
                is_self_only: Boolean(data?.is_self_only),
                linked: data?.linked !== false
            });
            if (data?.is_self_only && data?.bitrix_user_id) {
                setFilters(current => ({
                    ...current,
                    user: String(data.bitrix_user_id)
                }));
            }
        } catch (err) {
            console.warn('analytics scope', err);
        }
    }, [requestConfig]);

    useEffect(() => {
        loadAnalyticsScope();
    }, [loadAnalyticsScope]);

    const buildCurrentFilterPayload = useCallback(
        () => ({
            filters: {
                dateFrom: filters.dateFrom || null,
                dateTo: filters.dateTo || null,
                modifiedDateFrom: filters.modifiedDateFrom || null,
                modifiedDateTo: filters.modifiedDateTo || null,
                stage: filters.stage,
                semantic: filters.semantic,
                user: filters.user,
                source: filters.source
            },
            vacancyFilter,
            hasRecordingOnly,
            search,
            phoneFilter
        }),
        [
            filters,
            vacancyFilter,
            hasRecordingOnly,
            search,
            phoneFilter
        ]
    );

    const applyFilterPreset = useCallback(preset => {
        if (!preset) return;
        const payload = preset.payload || {};
        const nextFilters = payload.filters || {};
        const legacyModified = payload.dateType === 'modified';
        setFilters(current => ({
            dateFrom:
                legacyModified
                    ? dayjs().startOf('month').format('YYYY-MM-DD')
                    : nextFilters.dateFrom ||
                      dayjs().startOf('month').format('YYYY-MM-DD'),
            dateTo:
                legacyModified
                    ? dayjs().endOf('month').format('YYYY-MM-DD')
                    : nextFilters.dateTo ||
                      dayjs().endOf('month').format('YYYY-MM-DD'),
            modifiedDateFrom:
                nextFilters.modifiedDateFrom ||
                (legacyModified ? nextFilters.dateFrom : null) ||
                null,
            modifiedDateTo:
                nextFilters.modifiedDateTo ||
                (legacyModified ? nextFilters.dateTo : null) ||
                null,
            stage: nextFilters.stage || undefined,
            semantic: nextFilters.semantic || undefined,
            user:
                isSelfOnlyHr && selfBitrixUserId
                    ? selfBitrixUserId
                    : nextFilters.user || undefined,
            source: nextFilters.source || undefined
        }));
        setVacancyFilter(String(payload.vacancyFilter || ''));
        setHasRecordingOnly(Boolean(payload.hasRecordingOnly));
        setSearch(String(payload.search || ''));
        setPhoneFilter(String(payload.phoneFilter || ''));
        setSelectedPresetId(preset.id);
        message.success(`Применён фильтр «${preset.name}»`);
    }, [isSelfOnlyHr, selfBitrixUserId]);

    const saveFilterPreset = useCallback(async () => {
        const name = String(presetName || '').trim();
        if (!name) {
            message.warning('Укажите название фильтра');
            return;
        }
        setPresetSaving(true);
        try {
            const payload = buildCurrentFilterPayload();
            if (isSelfOnlyHr && selfBitrixUserId) {
                payload.filters = {
                    ...payload.filters,
                    user: selfBitrixUserId
                };
            }
            const {data} = await axios.post(
                `${API}/filter-presets`,
                {
                    name,
                    is_global: Boolean(
                        presetGlobal &&
                            canSaveGlobalPreset &&
                            analyticsScope.can_save_global_filters
                    ),
                    payload
                },
                requestConfig
            );
            message.success(
                data.is_global
                    ? 'Глобальный фильтр сохранён для всех'
                    : 'Фильтр сохранён'
            );
            setPresetModalOpen(false);
            setPresetName('');
            setPresetGlobal(false);
            await loadFilterPresets();
            if (data?.id) setSelectedPresetId(data.id);
        } catch (err) {
            message.error(
                err.response?.data?.error || 'Не удалось сохранить фильтр'
            );
        } finally {
            setPresetSaving(false);
        }
    }, [
        presetName,
        presetGlobal,
        canSaveGlobalPreset,
        analyticsScope.can_save_global_filters,
        isSelfOnlyHr,
        selfBitrixUserId,
        buildCurrentFilterPayload,
        requestConfig,
        loadFilterPresets
    ]);

    const deleteFilterPreset = useCallback(
        async presetId => {
            try {
                await axios.delete(
                    `${API}/filter-presets/${presetId}`,
                    requestConfig
                );
                message.success('Фильтр удалён');
                if (selectedPresetId === presetId) {
                    setSelectedPresetId(undefined);
                }
                await loadFilterPresets();
            } catch (err) {
                message.error(
                    err.response?.data?.error || 'Не удалось удалить фильтр'
                );
            }
        },
        [requestConfig, selectedPresetId, loadFilterPresets]
    );

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        loadFilterPresets();
    }, [loadFilterPresets]);

    useEffect(() => {
        paneLayoutRef.current = paneLayout;
    }, [paneLayout]);

    useEffect(() => {
        filterVisibleRef.current = filterVisible;
    }, [filterVisible]);

    useEffect(() => {
        dealsVisibleRef.current = dealsVisible;
    }, [dealsVisible]);

    useEffect(() => {
        boardViewRef.current = boardView;
    }, [boardView]);

    const persistView = useCallback((patch = {}) => {
        const next = {
            boardView: boardViewRef.current,
            filterVisible: filterVisibleRef.current,
            dealsVisible: dealsVisibleRef.current,
            paneLayout: {
                ...paneLayoutRef.current,
                ...(patch.paneLayout || {})
            }
        };

        if (patch.boardView != null) next.boardView = patch.boardView;
        if (patch.filterVisible != null) next.filterVisible = patch.filterVisible;
        if (patch.dealsVisible != null) next.dealsVisible = patch.dealsVisible;

        try {
            localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
        } catch {
            // ignore quota / private mode
        }
    }, []);

    const setFilterVisiblePersist = value => {
        setFilterVisible(value);
        filterVisibleRef.current = value;
        persistView({filterVisible: value});
    };

    const setDealsVisiblePersist = value => {
        setDealsVisible(value);
        dealsVisibleRef.current = value;
        persistView({dealsVisible: value});
    };

    const setBoardViewPersist = value => {
        setBoardView(value);
        boardViewRef.current = value;
        persistView({boardView: value});
    };

    const filteredDeals = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        const phoneNeedle = onlyPhoneDigits(phoneFilter);
        const vacancyNeedle = vacancyFilter.trim().toLowerCase();

        return deals.filter(deal => {
            if (hasRecordingOnly && !deal.has_recording) return false;
            if (phoneNeedle && !phonesOverlap(deal.contact_phone, phoneNeedle)) {
                return false;
            }
            if (vacancyNeedle) {
                const matched = [deal.vacancy_name, deal.vacancy_id]
                    .filter(Boolean)
                    .some(value =>
                        String(value).toLowerCase().includes(vacancyNeedle)
                    );
                if (!matched) return false;
            }
            if (!normalizedSearch) return true;
            return [
                deal.id,
                deal.title,
                deal.stage_name,
                deal.user_name,
                deal.source_name,
                deal.company_name,
                deal.contact_name,
                deal.contact_phone,
                deal.vacancy_name,
                deal.vacancy_id
            ].some(value =>
                String(value || '').toLowerCase().includes(normalizedSearch)
            );
        });
    }, [deals, search, phoneFilter, vacancyFilter, hasRecordingOnly]);

    const periodFunnelSummary = useMemo(() => {
        const total = filteredDeals.length;
        const stages = {};

        if (funnelTotals?.stages) {
            Object.entries(funnelTotals.stages).forEach(([key, val]) => {
                stages[key] = toNumber(val);
            });
        } else {
            Object.values(vacancyFunnelByName).forEach(row => {
                Object.entries(row.stages || {}).forEach(([name, count]) => {
                    stages[name] = (stages[name] || 0) + toNumber(count);
                });
            });
        }

        const incoming = funnelTotals
            ? toNumber(funnelTotals.incoming ?? stages['Входящие'])
            : Object.values(vacancyFunnelByName).reduce(
                  (sum, row) =>
                      sum +
                      toNumber(row.incoming || row.stages?.['Входящие']),
                  0
              ) || toNumber(stages['Входящие']);
        const success = funnelTotals
            ? toNumber(funnelTotals.hire ?? stages['Трудоустроен'])
            : toNumber(stages['Трудоустроен']);
        const failed = funnelTotals
            ? toNumber(funnelTotals.exit ?? stages['Выход'])
            : toNumber(stages['Выход']);
        const work = Math.max(0, total - success - failed);
        const conversion = total ? Number(((success / total) * 100).toFixed(1)) : 0;
        const closed = success + failed;
        const hire_rate = closed ? Number(((success / closed) * 100).toFixed(1)) : 0;
        const exit_rate = total ? Number(((failed / total) * 100).toFixed(1)) : 0;
        const calls_count = filteredDeals.reduce(
            (sum, item) => sum + toNumber(item.calls_count),
            0
        );
        const talk_duration_sec = filteredDeals.reduce(
            (sum, item) => sum + toNumber(item.talk_duration_sec),
            0
        );
        const recorded_count = filteredDeals.reduce(
            (sum, item) => sum + toNumber(item.recorded_count),
            0
        );
        const avg_duration_sec = calls_count
            ? Math.round(talk_duration_sec / calls_count)
            : 0;

        const stageBreakdown = STAGE_ORDER.map(stageName => ({
            name: stageName,
            short: STAGE_SHORT[stageName] || stageName,
            count: toNumber(stages[stageName]),
            color: (STAGE_META[stageName] || {}).color || '#8c8c8c'
        })).filter(stage => stage.count > 0);

        Object.keys(stages).forEach(stageName => {
            if (STAGE_ORDER.includes(stageName)) return;
            const count = toNumber(stages[stageName]);
            if (!count) return;
            stageBreakdown.push({
                name: stageName,
                short: truncateLabel(stageName, 16),
                count,
                color: '#8c8c8c'
            });
        });

        return {
            total,
            incoming,
            work,
            success,
            failed,
            conversion,
            hire_rate,
            exit_rate,
            calls_count,
            talk_duration_sec,
            recorded_count,
            avg_duration_sec,
            stale_work: summary.stale_work,
            movements_count: summary.movements_count || 0,
            stageBreakdown,
            loaded:
                Boolean(funnelTotals) ||
                Object.keys(vacancyFunnelByName).length > 0
        };
    }, [
        filteredDeals,
        funnelTotals,
        vacancyFunnelByName,
        summary.stale_work,
        summary.movements_count
    ]);

    const recruiterComputed = useMemo(() => {
        const grouped = new Map();
        const now = dayjs();
        filteredDeals.forEach(deal => {
            const id = String(deal.assigned_by_id || '0');
            if (!grouped.has(id)) {
                grouped.set(id, {
                    assigned_by_id: id,
                    user_name: deal.user_name || '—',
                    total: 0,
                    work: 0,
                    success: 0,
                    failed: 0,
                    calls_count: 0,
                    recorded_count: 0,
                    talk_duration_sec: 0,
                    movements_count: 0,
                    lifetime_days_sum: 0
                });
            }
            const row = grouped.get(id);
            row.total += 1;
            const semantic = getSemanticId(deal);
            if (semantic === 'S') row.success += 1;
            else if (semantic === 'F') row.failed += 1;
            else row.work += 1;
            row.calls_count += toNumber(deal.calls_count);
            row.recorded_count += toNumber(deal.recorded_count);
            row.talk_duration_sec += toNumber(deal.talk_duration_sec);
            const started = dayjs(deal.vacancy_applied_at || deal.date_create);
            const ended = dayjs(deal.close_date || deal.date_modify || now);
            row.lifetime_days_sum +=
                started.isValid() && ended.isValid()
                    ? Math.max(0, ended.diff(started, 'day', true))
                    : 0;
        });

        const apiById = new Map(
            recruiterStats.map(item => [String(item.assigned_by_id), item])
        );

        return Array.from(grouped.values())
            .map(item => {
                const api = apiById.get(item.assigned_by_id);
                const closed = item.success + item.failed;
                const callsFromApi = api ? toNumber(api.calls_count) : 0;
                return {
                    ...item,
                    calls_count: Math.max(item.calls_count, callsFromApi),
                    recorded_count: Math.max(
                        item.recorded_count,
                        api ? toNumber(api.recorded_count) : 0
                    ),
                    talk_duration_sec: Math.max(
                        item.talk_duration_sec,
                        api ? toNumber(api.talk_duration_sec) : 0
                    ),
                    movements_count: api
                        ? toNumber(api.movements_count)
                        : 0,
                    exit_rate: item.total
                        ? Number(((item.failed / item.total) * 100).toFixed(1))
                        : 0,
                    conversion: item.total
                        ? Number(((item.success / item.total) * 100).toFixed(1))
                        : 0,
                    hire_rate: closed
                        ? Number(((item.success / closed) * 100).toFixed(1))
                        : 0,
                    avg_lifetime_days: item.total
                        ? Number((item.lifetime_days_sum / item.total).toFixed(1))
                        : 0
                };
            })
            .sort((a, b) => b.success - a.success || b.total - a.total);
    }, [filteredDeals, recruiterStats]);

    const openHrSheet = useCallback(hrId => {
        setSelectedHrId(String(hrId));
        setHrSheetOpen(true);
        setDealOpen(false);
        setHrCalls([]);
        setHrCallsError('');
    }, []);

    const closeHrSheet = useCallback(() => {
        setHrSheetOpen(false);
        setSelectedHrId(null);
        setHrCalls([]);
        setHrCallsError('');
        setActiveAudioName('');
        if (audioObjectUrlRef.current) {
            URL.revokeObjectURL(audioObjectUrlRef.current);
            audioObjectUrlRef.current = null;
        }
        setAudioUrls({});
    }, []);

    const hrDetail = useMemo(() => {
        if (!selectedHrId) return null;
        const summary =
            recruiterComputed.find(
                item => String(item.assigned_by_id) === String(selectedHrId)
            ) || null;
        const deals = filteredDeals.filter(
            deal => String(deal.assigned_by_id || '0') === String(selectedHrId)
        );
        if (!summary && !deals.length) return null;

        const now = dayjs();
        const byVacancyMap = new Map();
        const byStageMap = new Map();
        const bySourceMap = new Map();
        const byDayMap = new Map();
        let withPhone = 0;
        let withRecording = 0;
        let withoutVacancy = 0;

        deals.forEach(deal => {
            const vacancy = String(deal.vacancy_name || '').trim() || 'Без вакансии';
            if (vacancy === 'Без вакансии') withoutVacancy += 1;

            if (!byVacancyMap.has(vacancy)) {
                byVacancyMap.set(vacancy, {
                    name: vacancy,
                    total: 0,
                    work: 0,
                    success: 0,
                    failed: 0,
                    calls_count: 0,
                    talk_duration_sec: 0,
                    recorded_count: 0
                });
            }
            const vac = byVacancyMap.get(vacancy);
            vac.total += 1;
            const sem = getSemanticId(deal);
            if (sem === 'S') vac.success += 1;
            else if (sem === 'F') vac.failed += 1;
            else vac.work += 1;
            vac.calls_count += toNumber(deal.calls_count);
            vac.talk_duration_sec += toNumber(deal.talk_duration_sec);
            vac.recorded_count += toNumber(deal.recorded_count);

            const stageName = deal.stage_name || 'Без стадии';
            if (!byStageMap.has(stageName)) {
                byStageMap.set(stageName, {
                    name: stageName,
                    total: 0,
                    color: (STAGE_META[stageName] || {}).color || '#8c8c8c'
                });
            }
            byStageMap.get(stageName).total += 1;

            const sourceName = deal.source_name || 'Без источника';
            if (!bySourceMap.has(sourceName)) {
                bySourceMap.set(sourceName, {
                    name: sourceName,
                    total: 0,
                    success: 0,
                    failed: 0
                });
            }
            const src = bySourceMap.get(sourceName);
            src.total += 1;
            if (sem === 'S') src.success += 1;
            if (sem === 'F') src.failed += 1;

            const day = dayjs(deal.date_create);
            if (day.isValid()) {
                const key = day.format('YYYY-MM-DD');
                if (!byDayMap.has(key)) {
                    byDayMap.set(key, {
                        day: key,
                        label: day.format('DD.MM'),
                        total: 0,
                        work: 0,
                        success: 0,
                        failed: 0
                    });
                }
                const row = byDayMap.get(key);
                row.total += 1;
                if (sem === 'S') row.success += 1;
                else if (sem === 'F') row.failed += 1;
                else row.work += 1;
            }

            if (deal.contact_phone) withPhone += 1;
            if (toNumber(deal.recorded_count) > 0 || deal.has_recording) {
                withRecording += 1;
            }
        });

        const withRates = rows =>
            rows.map(item => {
                const closed = item.success + item.failed;
                return {
                    ...item,
                    conversion: item.total
                        ? Number(((item.success / item.total) * 100).toFixed(1))
                        : 0,
                    exit_rate: item.total
                        ? Number(((item.failed / item.total) * 100).toFixed(1))
                        : 0,
                    hire_rate: closed
                        ? Number(((item.success / closed) * 100).toFixed(1))
                        : 0
                };
            });

        const by_vacancy = withRates(Array.from(byVacancyMap.values())).sort(
            (a, b) => b.total - a.total || b.success - a.success
        );
        const by_stage = Array.from(byStageMap.values()).sort((a, b) => {
            const first = STAGE_ORDER.indexOf(a.name);
            const second = STAGE_ORDER.indexOf(b.name);
            return (first === -1 ? 999 : first) - (second === -1 ? 999 : second);
        });
        const by_source = withRates(Array.from(bySourceMap.values())).sort(
            (a, b) => b.total - a.total
        );
        const by_day = Array.from(byDayMap.values())
            .sort((a, b) => a.day.localeCompare(b.day))
            .map(row => ({
                ...row,
                conversion: row.total
                    ? Number(((row.success / row.total) * 100).toFixed(1))
                    : 0
            }));

        const semantic = [
            {
                key: 'P',
                name: SEMANTIC_META.P.name,
                value: summary?.work || 0,
                color: CHART_COLORS.work
            },
            {
                key: 'S',
                name: SEMANTIC_META.S.name,
                value: summary?.success || 0,
                color: CHART_COLORS.success
            },
            {
                key: 'F',
                name: SEMANTIC_META.F.name,
                value: summary?.failed || 0,
                color: CHART_COLORS.failed
            }
        ].filter(item => item.value > 0);

        const avg_duration_sec =
            summary?.calls_count > 0
                ? Math.round(
                      (summary.talk_duration_sec || 0) / summary.calls_count
                  )
                : 0;

        const recentDeals = [...deals]
            .sort((a, b) => {
                const da = dayjs(a.date_modify || a.date_create);
                const db = dayjs(b.date_modify || b.date_create);
                if (!da.isValid()) return 1;
                if (!db.isValid()) return -1;
                return db.valueOf() - da.valueOf();
            })
            .slice(0, 30)
            .map(deal => {
                const started = dayjs(deal.vacancy_applied_at || deal.date_create);
                const ended = dayjs(deal.close_date || deal.date_modify || now);
                const lifetime_days =
                    started.isValid() && ended.isValid()
                        ? Math.max(0, Math.round(ended.diff(started, 'day', true)))
                        : null;
                return {
                    id: deal.id,
                    title: deal.title || `Сделка #${deal.id}`,
                    vacancy_name: deal.vacancy_name || '—',
                    stage_name: deal.stage_name || '—',
                    semantic: getSemanticId(deal),
                    calls_count: toNumber(deal.calls_count),
                    talk_duration_sec: toNumber(deal.talk_duration_sec),
                    recorded_count: toNumber(deal.recorded_count),
                    date_create: deal.date_create,
                    lifetime_days,
                    contact_phone: deal.contact_phone || ''
                };
            });

        return {
            summary: {
                ...(summary || {
                    assigned_by_id: selectedHrId,
                    user_name: deals[0]?.user_name || '—',
                    total: deals.length,
                    work: 0,
                    success: 0,
                    failed: 0,
                    calls_count: 0,
                    recorded_count: 0,
                    talk_duration_sec: 0,
                    movements_count: 0,
                    conversion: 0,
                    exit_rate: 0,
                    hire_rate: 0,
                    avg_lifetime_days: 0
                }),
                avg_duration_sec,
                with_phone: withPhone,
                with_recording: withRecording,
                without_vacancy: withoutVacancy,
                vacancies_count: by_vacancy.filter(v => v.name !== 'Без вакансии')
                    .length,
                touches_per_deal:
                    summary?.total > 0
                        ? Number(
                              (
                                  (summary.movements_count || 0) / summary.total
                              ).toFixed(2)
                          )
                        : 0,
                calls_per_deal:
                    summary?.total > 0
                        ? Number(
                              (
                                  (summary.calls_count || 0) / summary.total
                              ).toFixed(2)
                          )
                        : 0
            },
            by_vacancy,
            by_stage,
            by_source,
            by_day,
            semantic,
            recentDeals,
            deals,
            vacancyChart: by_vacancy.slice(0, 10).map(item => ({
                name: truncateLabel(item.name, 16),
                fullName: item.name,
                total: item.total,
                success: item.success,
                failed: item.failed,
                conversion: item.conversion
            })),
            stageChart: by_stage.map(item => ({
                name: truncateLabel(item.name, 14),
                fullName: item.name,
                total: item.total,
                color: item.color
            }))
        };
    }, [selectedHrId, filteredDeals, recruiterComputed]);

    const vacancyComputed = useMemo(() => {
        const activeNames = new Set(
            vacancyDirectory
                .filter(item => item.is_active)
                .map(item => String(item.name || '').trim())
                .filter(Boolean)
        );
        // Если справочник ещё не загружен — берём имена из фильтра (активные)
        const allowAllFromFilter =
            !vacancyDirectory.length && vacancyOptions.length > 0;
        if (allowAllFromFilter) {
            vacancyOptions.forEach(opt => {
                const n = String(opt.value || opt.label || '').trim();
                if (n) activeNames.add(n);
            });
        }

        const grouped = new Map();
        const now = dayjs();
        filteredDeals.forEach(deal => {
            const name = String(deal.vacancy_name || '').trim();
            if (!name) return;
            if (activeNames.size && !activeNames.has(name)) return;
            if (!grouped.has(name)) {
                grouped.set(name, {
                    name,
                    total: 0,
                    work: 0,
                    success: 0,
                    failed: 0,
                    incoming: 0,
                    calls_count: 0,
                    recorded_count: 0,
                    talk_duration_sec: 0,
                    lifetime_days_sum: 0,
                    stages: {}
                });
            }
            const row = grouped.get(name);
            row.total += 1;
            const semantic = getSemanticId(deal);
            if (semantic === 'S') row.success += 1;
            else if (semantic === 'F') row.failed += 1;
            else row.work += 1;
            row.calls_count += toNumber(deal.calls_count);
            row.recorded_count += toNumber(deal.recorded_count);
            row.talk_duration_sec += toNumber(deal.talk_duration_sec);

            const started = dayjs(deal.vacancy_applied_at || deal.date_create);
            const ended = dayjs(deal.close_date || deal.date_modify || now);
            row.lifetime_days_sum +=
                started.isValid() && ended.isValid()
                    ? Math.max(0, ended.diff(started, 'day', true))
                    : 0;

            const stageName = deal.stage_name || 'Без стадии';
            row.stages[stageName] = (row.stages[stageName] || 0) + 1;
            if (stageName === 'Входящие') {
                row.incoming += 1;
            }
        });

        const dirByName = new Map(
            vacancyDirectory.map(item => [
                String(item.name || '').trim(),
                item
            ])
        );

        return Array.from(grouped.values())
            .map(item => {
                const meta = dirByName.get(item.name);
                const funnel = vacancyFunnelByName[item.name];
                const funnelStages = funnel?.stages || null;
                const stageSource = funnelStages || item.stages;
                const stageBreakdown = STAGE_ORDER.map(stageName => ({
                    name: stageName,
                    short: STAGE_SHORT[stageName] || stageName,
                    count: toNumber(stageSource[stageName]),
                    color: (STAGE_META[stageName] || {}).color || '#8c8c8c'
                })).filter(stage => stage.count > 0);

                // стадии вне канонического списка (если есть)
                Object.keys(stageSource).forEach(stageName => {
                    if (STAGE_ORDER.includes(stageName)) return;
                    const count = toNumber(stageSource[stageName]);
                    if (!count) return;
                    stageBreakdown.push({
                        name: stageName,
                        short: truncateLabel(stageName, 16),
                        count,
                        color: '#8c8c8c'
                    });
                });

                // Шапка карточки = та же воронка, что и список стадий
                // (сколько дошли до стадии), а не текущий semantic.
                const incoming = funnel
                    ? toNumber(
                          funnel.incoming ||
                              funnelStages?.['Входящие']
                      )
                    : item.incoming;
                const success = funnel
                    ? toNumber(funnelStages?.['Трудоустроен'])
                    : item.success;
                const failed = funnel
                    ? toNumber(funnelStages?.['Выход'])
                    : item.failed;
                const closed = success + failed;

                return {
                    ...item,
                    incoming,
                    success,
                    failed,
                    id: meta?.id || null,
                    stageBreakdown,
                    funnelLoaded: Boolean(funnel),
                    avg_lifetime_days: item.total
                        ? Number((item.lifetime_days_sum / item.total).toFixed(1))
                        : 0,
                    exit_rate: item.total
                        ? Number(((failed / item.total) * 100).toFixed(1))
                        : 0,
                    conversion: item.total
                        ? Number(((success / item.total) * 100).toFixed(1))
                        : 0,
                    hire_rate: closed
                        ? Number(((success / closed) * 100).toFixed(1))
                        : 0
                };
            })
            .sort((a, b) => b.success - a.success || b.total - a.total);
    }, [filteredDeals, vacancyDirectory, vacancyOptions, vacancyFunnelByName]);

    // Prefetch active vacancies for tiles when page loads
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const {data} = await axios.get(
                    `${API}/vacancies/directory?activeOnly=true`,
                    requestConfig
                );
                if (!cancelled) setVacancyDirectory(toArray(data));
            } catch {
                /* optional prefetch */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [requestConfig]);

    const kanbanColumns = useMemo(() => {
        const map = new Map();
        filteredDeals.forEach(deal => {
            const name = deal.stage_name || 'Без стадии';
            const id = deal.stage_id || name;
            if (!map.has(id)) {
                map.set(id, {
                    id,
                    name,
                    color: (STAGE_META[name] || {}).color || '#8c8c8c',
                    deals: []
                });
            }
            map.get(id).deals.push(deal);
        });

        return Array.from(map.values()).sort((a, b) => {
            const first = STAGE_ORDER.indexOf(a.name);
            const second = STAGE_ORDER.indexOf(b.name);
            return (first === -1 ? 999 : first) - (second === -1 ? 999 : second);
        });
    }, [filteredDeals]);

    const semanticChartData = useMemo(() => {
        const work = filteredDeals.filter(item => getSemanticId(item) === 'P').length;
        const success = filteredDeals.filter(
            item => getSemanticId(item) === 'S'
        ).length;
        const failed = filteredDeals.filter(
            item => getSemanticId(item) === 'F'
        ).length;

        return [
            {
                key: 'P',
                name: SEMANTIC_META.P.name,
                value: work,
                color: CHART_COLORS.work
            },
            {
                key: 'S',
                name: SEMANTIC_META.S.name,
                value: success,
                color: CHART_COLORS.success
            },
            {
                key: 'F',
                name: SEMANTIC_META.F.name,
                value: failed,
                color: CHART_COLORS.failed
            }
        ].filter(item => item.value > 0);
    }, [filteredDeals]);

    const stageFunnelData = useMemo(
        () =>
            kanbanColumns.map(column => ({
                name: column.name,
                short: truncateLabel(column.name, 14),
                count: column.deals.length,
                color: column.color
            })),
        [kanbanColumns]
    );

    const dailyTrendData = useMemo(() => {
        const buckets = new Map();
        filteredDeals.forEach(deal => {
            const day = dayjs(deal.date_create);
            if (!day.isValid()) return;
            const key = day.format('YYYY-MM-DD');
            if (!buckets.has(key)) {
                buckets.set(key, {
                    day: key,
                    label: day.format('DD.MM'),
                    total: 0,
                    work: 0,
                    success: 0,
                    failed: 0
                });
            }
            const row = buckets.get(key);
            row.total += 1;
            const sem = getSemanticId(deal);
            if (sem === 'S') row.success += 1;
            else if (sem === 'F') row.failed += 1;
            else row.work += 1;
        });

        return Array.from(buckets.values())
            .sort((a, b) => a.day.localeCompare(b.day))
            .map(row => ({
                ...row,
                conversion: row.total
                    ? Number(((row.success / row.total) * 100).toFixed(1))
                    : 0,
                exit_rate: row.total
                    ? Number(((row.failed / row.total) * 100).toFixed(1))
                    : 0
            }));
    }, [filteredDeals]);

    const hrConversionChartData = useMemo(
        () =>
            recruiterComputed.slice(0, 12).map(item => ({
                name: truncateLabel(item.user_name, 16),
                fullName: item.user_name,
                total: item.total,
                success: item.success,
                failed: item.failed,
                conversion: item.conversion,
                exit_rate: item.exit_rate
            })),
        [recruiterComputed]
    );

    const vacancyConversionChartData = useMemo(
        () =>
            vacancyComputed.slice(0, 12).map(item => ({
                name: truncateLabel(item.name, 16),
                fullName: item.name,
                total: item.total,
                success: item.success,
                failed: item.failed,
                conversion: item.conversion,
                exit_rate: item.exit_rate
            })),
        [vacancyComputed]
    );

    const resetFilters = () => {
        setFilters({
            dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
            dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
            modifiedDateFrom: null,
            modifiedDateTo: null,
            stage: undefined,
            semantic: undefined,
            user:
                isSelfOnlyHr && selfBitrixUserId
                    ? selfBitrixUserId
                    : undefined,
            source: undefined
        });
        setSearch('');
        setPhoneFilter('');
        setVacancyFilter('');
        setHasRecordingOnly(false);
        setSelectedPresetId(undefined);
    };

    const clearActiveAudio = useCallback(() => {
        if (audioObjectUrlRef.current) {
            URL.revokeObjectURL(audioObjectUrlRef.current);
            audioObjectUrlRef.current = null;
        }
        setAudioUrls({});
        setActiveAudioName('');
        setAudioLoadingName('');
    }, []);

    const closeDealSheet = () => {
        setDealOpen(false);
        setCurrentDeal(null);
        setCommentText('');
        setDealCalls([]);
        setDealCallsError('');
        clearActiveAudio();
        editForm.resetFields();
    };

    const loadRecordingAudio = useCallback(async filename => {
        if (!filename) return null;
        if (
            activeAudioName === filename &&
            audioObjectUrlRef.current
        ) {
            return audioObjectUrlRef.current;
        }

        setAudioLoadingName(filename);
        try {
            const response = await axios.get('/api/ats/call-records/file', {
                headers: authHeaders(),
                params: {filename},
                responseType: 'blob',
                timeout: 60000
            });
            if (audioObjectUrlRef.current) {
                URL.revokeObjectURL(audioObjectUrlRef.current);
                audioObjectUrlRef.current = null;
            }
            const blobUrl = URL.createObjectURL(response.data);
            audioObjectUrlRef.current = blobUrl;
            setAudioUrls({[filename]: blobUrl});
            setActiveAudioName(filename);
            return blobUrl;
        } finally {
            setAudioLoadingName('');
        }
    }, [activeAudioName]);

    const loadDealCalls = async (dealId, {refresh = true} = {}) => {
        if (!dealId) return;
        setDealCalls([]);
        setDealCallsError('');
        setDealCallsLoading(true);
        clearActiveAudio();
        try {
            const response = await axios.get(
                `/api/ats/deals/${dealId}/calls`,
                {
                    headers: authHeaders(),
                    params: {refresh: refresh ? '1' : '0'}
                }
            );
            setDealCalls(
                Array.isArray(response.data?.calls)
                    ? response.data.calls
                    : []
            );
            if (response.data?.warning) {
                setDealCallsError(response.data.warning);
            }
        } catch (callsError) {
            setDealCallsError(
                callsError.response?.data?.error ||
                    callsError.message ||
                    'Не удалось загрузить звонки АТС'
            );
        } finally {
            setDealCallsLoading(false);
        }
    };

    const loadHrCalls = useCallback(
        async (deals = []) => {
            const phones = [
                ...new Set(
                    (deals || [])
                        .map(deal => onlyPhoneDigits(deal.contact_phone))
                        .filter(Boolean)
                )
            ].slice(0, 300);

            if (!phones.length) {
                setHrCalls([]);
                setHrCallsError('');
                return;
            }

            setHrCallsLoading(true);
            setHrCallsError('');
            try {
                const params = new URLSearchParams();
                params.set('phones', phones.join(','));
                params.set('limit', '500');
                if (filters.dateFrom) {
                    params.set('dateFrom', `${filters.dateFrom} 00:00:00`);
                }
                if (filters.dateTo) {
                    params.set('dateTo', `${filters.dateTo} 23:59:59`);
                }

                const response = await axios.get(
                    `/api/ats/calls/local-batch?${params.toString()}`,
                    {headers: authHeaders(), timeout: 30000}
                );

                const phoneToDeal = new Map();
                (deals || []).forEach(deal => {
                    const key = phoneIndexKey(deal.contact_phone);
                    if (!key) return;
                    if (!phoneToDeal.has(key)) {
                        phoneToDeal.set(key, {
                            deal_id: deal.id,
                            deal_title: deal.title || `Сделка #${deal.id}`,
                            vacancy_name: deal.vacancy_name || '',
                            contact_phone: deal.contact_phone || ''
                        });
                    }
                });

                const matched = (Array.isArray(response.data?.calls)
                    ? response.data.calls
                    : []
                ).map((call, index) => {
                    const keys = [
                        phoneIndexKey(call.caller_number),
                        phoneIndexKey(call.callee_number),
                        phoneIndexKey(call.destination_number)
                    ].filter(Boolean);
                    let dealMeta = null;
                    for (const key of keys) {
                        if (phoneToDeal.has(key)) {
                            dealMeta = phoneToDeal.get(key);
                            break;
                        }
                    }
                    return {
                        ...call,
                        row_key:
                            call.uuid ||
                            `${call.record_file_name || 'call'}_${call.date || index}`,
                        deal_id: dealMeta?.deal_id || call.deal_id || null,
                        deal_title: dealMeta?.deal_title || null,
                        vacancy_name: dealMeta?.vacancy_name || null,
                        matched_phone: dealMeta?.contact_phone || null
                    };
                });

                setHrCalls(matched);
            } catch (callsError) {
                setHrCalls([]);
                setHrCallsError(
                    callsError.response?.data?.error ||
                        callsError.message ||
                        'Не удалось загрузить звонки HR'
                );
            } finally {
                setHrCallsLoading(false);
            }
        },
        [filters.dateFrom, filters.dateTo]
    );

    useEffect(() => {
        if (!hrSheetOpen || !selectedHrId || !hrDetail?.deals?.length) {
            return undefined;
        }
        loadHrCalls(hrDetail.deals);
        return undefined;
    }, [
        hrSheetOpen,
        selectedHrId,
        hrDetail,
        loadHrCalls
    ]);

    const openDealDetails = async deal => {
        if (!deal?.id) return;

        closeHrSheet();
        setDealOpen(true);
        setDealLoading(true);
        setCommentText('');
        setDealCalls([]);
        setDealCallsError('');
        loadDealCalls(deal.id, {refresh: true});

        try {
            const response = await axios.get(
                `/api/hr/deals/${deal.id}`,
                {headers: authHeaders()}
            );
            const opened = response.data;
            setCurrentDeal(opened);
            editForm.setFieldsValue({
                title: opened.title,
                stage_id: opened.stage_id,
                assigned_by_id: opened.assigned_by_id
                    ? String(opened.assigned_by_id)
                    : undefined,
                city: opened.city,
                contact_phone: opened.contact_phone,
                source_id: opened.source_id
            });
        } catch (openError) {
            console.error(openError);
            message.error(
                openError.response?.data?.message ||
                    'Не удалось открыть сделку'
            );
            closeDealSheet();
        } finally {
            setDealLoading(false);
        }
    };

    const saveDeal = async () => {
        if (!currentDeal?.id) return;

        try {
            const values = await editForm.validateFields();
            setSavingDeal(true);

            const stageChanged =
                values.stage_id &&
                String(values.stage_id) !== String(currentDeal.stage_id);

            await axios.put(
                `/api/hr/deals/${currentDeal.id}`,
                values,
                {headers: authHeaders()}
            );

            if (stageChanged) {
                await axios.put(
                    `/api/hr/deals/${currentDeal.id}/move`,
                    {stage_id: values.stage_id},
                    {headers: authHeaders()}
                );
            }

            message.success('Сделка сохранена');
            await openDealDetails({id: currentDeal.id});
            await loadData();
        } catch (saveError) {
            if (saveError?.errorFields) return;
            message.error(
                saveError.response?.data?.message ||
                    'Не удалось сохранить'
            );
        } finally {
            setSavingDeal(false);
        }
    };

    const addComment = async () => {
        if (!currentDeal?.id || !commentText.trim()) return;

        setCommentSaving(true);
        try {
            await axios.post(
                `/api/hr/deals/${currentDeal.id}/comments`,
                {text: commentText.trim()},
                {headers: authHeaders()}
            );
            setCommentText('');
            await openDealDetails({id: currentDeal.id});
            message.success('Комментарий добавлен');
        } catch (commentError) {
            message.error(
                commentError.response?.data?.message ||
                    'Не удалось добавить комментарий'
            );
        } finally {
            setCommentSaving(false);
        }
    };

    const exportExcel = () => {
        if (!filteredDeals.length) {
            message.warning('Нет сделок для выгрузки');
            return;
        }

        const dealsSheet = XLSX.utils.json_to_sheet(
            filteredDeals.map(item => {
                const semanticMeta =
                    SEMANTIC_META[getSemanticId(item)] || SEMANTIC_META.P;
                return {
                    ID: item.id,
                    'Кандидат / сделка': item.title || '',
                    Стадия: item.stage_name || '',
                    'Группа стадии': semanticMeta.name,
                    HR: item.user_name || '',
                    Вакансия: item.vacancy_name || '',
                    Источник: item.source_name || '',
                    Телефон: item.contact_phone || '',
                    'Дата создания': formatDate(item.date_create)
                };
            })
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, dealsSheet, 'Сделки');
        const period =
            filters.dateFrom && filters.dateTo
                ? `${filters.dateFrom}_${filters.dateTo}`
                : dayjs().format('YYYY-MM-DD');
        XLSX.writeFile(workbook, `HR_analytics_${period}.xlsx`);
    };

    const startPaneResize = (side, event) => {
        event.preventDefault();
        const startX = event.clientX;
        const startValue =
            side === 'filter'
                ? paneLayoutRef.current.filterPx
                : paneLayoutRef.current.dealsPx;

        document.body.classList.add('ba-pane-resizing');

        const onMove = moveEvent => {
            const delta = moveEvent.clientX - startX;
            if (side === 'filter') {
                const nextFilter = clampPane(startValue + delta, 220, 420);
                const next = {
                    ...paneLayoutRef.current,
                    filterPx: nextFilter
                };
                paneLayoutRef.current = next;
                setPaneLayout(next);
            } else {
                const nextDeals = clampPane(startValue - delta, 300, 640);
                const next = {
                    ...paneLayoutRef.current,
                    dealsPx: nextDeals
                };
                paneLayoutRef.current = next;
                setPaneLayout(next);
            }
        };

        const onUp = () => {
            document.body.classList.remove('ba-pane-resizing');
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            persistView({
                paneLayout: {...paneLayoutRef.current}
            });
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const periodLabel =
        filters.dateFrom && filters.dateTo
            ? `${dayjs(filters.dateFrom).format('DD.MM.YYYY')} — ${dayjs(filters.dateTo).format('DD.MM.YYYY')}`
            : 'весь период';

    const modifiedPeriodLabel =
        filters.modifiedDateFrom && filters.modifiedDateTo
            ? `${dayjs(filters.modifiedDateFrom).format('DD.MM.YYYY')} — ${dayjs(filters.modifiedDateTo).format('DD.MM.YYYY')}`
            : null;

    const renderDealCard = deal => {
        const stageName = deal.stage_name || 'Без стадии';
        const meta = STAGE_META[stageName] || {tag: 'default', color: '#8c8c8c'};
        const semanticMeta =
            SEMANTIC_META[getSemanticId(deal)] || SEMANTIC_META.P;

        return (
            <Card
                key={deal.id}
                size="small"
                className={`ba-deal-card${
                    currentDeal && String(currentDeal.id) === String(deal.id)
                        ? ' is-active'
                        : ''
                }`}
                styles={{body: {padding: 12}}}
                style={{borderLeft: `3px solid ${meta.color}`}}
                onClick={() => openDealDetails(deal)}
            >
                <div className="ba-deal-card__title">
                    {deal.title || `Сделка #${deal.id}`}
                </div>
                <div className="ba-deal-card__meta">
                    <div className="ba-deal-card__meta-row">
                        <Tag color={meta.tag} style={{margin: 0}}>
                            {stageName}
                        </Tag>
                    </div>
                    <div className="ba-deal-card__meta-row">
                        <UserOutlined/>
                        <span>{deal.user_name || 'HR не назначен'}</span>
                    </div>
                    {deal.vacancy_name ? (
                        <div className="ba-deal-card__meta-row">
                            <span>{deal.vacancy_name}</span>
                        </div>
                    ) : null}
                    {deal.contact_phone ? (
                        <div className="ba-deal-card__meta-row">
                            <PhoneOutlined/>
                            <span>{deal.contact_phone}</span>
                        </div>
                    ) : null}
                    <div className="ba-deal-card__meta-row">
                        <Tag color={semanticMeta.tag} style={{margin: 0}}>
                            {semanticMeta.name}
                        </Tag>
                        <span style={{marginLeft: 'auto'}}>
                            {dayjs(deal.date_create).isValid()
                                ? dayjs(deal.date_create).format('DD.MM')
                                : ''}
                        </span>
                    </div>
                </div>
            </Card>
        );
    };

    const filterPanel = (
        <aside className="ba-filter">
            <div className="ba-filter__header">
                <div>
                    <span className="ba-filter__kicker">HR Analytics</span>
                    <Title level={5} className="ba-filter__title">
                        Фильтры
                    </Title>
                </div>
                <Button
                    type="text"
                    icon={<MenuFoldOutlined/>}
                    onClick={() => setFilterVisiblePersist(false)}
                    title="Скрыть фильтр"
                />
            </div>
            <div className="ba-filter__body">
                <div className="ba-filter-field ba-filter-presets">
                    <span className="ba-filter-label">Сохранённые фильтры</span>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Выберите сохранённый"
                        value={selectedPresetId}
                        style={{width: '100%'}}
                        options={filterPresets.map(item => ({
                            value: item.id,
                            label: item.is_global
                                ? `${item.name} · общий`
                                : item.name
                        }))}
                        onChange={value => {
                            if (!value) {
                                setSelectedPresetId(undefined);
                                return;
                            }
                            const preset = filterPresets.find(
                                item => item.id === value
                            );
                            if (preset) applyFilterPreset(preset);
                        }}
                    />
                    <Space size={6} style={{marginTop: 8}} wrap>
                        <Button
                            size="small"
                            icon={<SaveOutlined/>}
                            onClick={() => {
                                setPresetName('');
                                setPresetGlobal(false);
                                setPresetModalOpen(true);
                            }}
                        >
                            Сохранить
                        </Button>
                        {selectedPresetId ? (
                            <Popconfirm
                                title="Удалить сохранённый фильтр?"
                                disabled={
                                    !filterPresets.find(
                                        item => item.id === selectedPresetId
                                    )?.can_delete
                                }
                                onConfirm={() =>
                                    deleteFilterPreset(selectedPresetId)
                                }
                            >
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined/>}
                                    disabled={
                                        !filterPresets.find(
                                            item => item.id === selectedPresetId
                                        )?.can_delete
                                    }
                                >
                                    Удалить
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                    {canSaveGlobalPreset || analyticsScope.can_save_global_filters ? (
                        <Text
                            type="secondary"
                            style={{display: 'block', marginTop: 6, fontSize: 11}}
                        >
                            Как руководитель можете сохранить фильтр для всех
                        </Text>
                    ) : (
                        <Text
                            type="secondary"
                            style={{display: 'block', marginTop: 6, fontSize: 11}}
                        >
                            {isSelfOnlyHr
                                ? 'Фильтры только по вашим сделкам'
                                : 'Личные фильтры + общие от руководителя'}
                        </Text>
                    )}
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Дата создания</span>
                    <RangePicker
                        allowClear
                        format="DD.MM.YYYY"
                        presets={DATE_PRESETS}
                        style={{width: '100%'}}
                        value={
                            filters.dateFrom && filters.dateTo
                                ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
                                : null
                        }
                        onChange={dates => {
                            setFilters(current => ({
                                ...current,
                                dateFrom: dates?.[0]?.format('YYYY-MM-DD') || null,
                                dateTo: dates?.[1]?.format('YYYY-MM-DD') || null
                            }));
                        }}
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Дата изменения</span>
                    <RangePicker
                        allowClear
                        format="DD.MM.YYYY"
                        presets={DATE_PRESETS}
                        style={{width: '100%'}}
                        value={
                            filters.modifiedDateFrom && filters.modifiedDateTo
                                ? [
                                      dayjs(filters.modifiedDateFrom),
                                      dayjs(filters.modifiedDateTo)
                                  ]
                                : null
                        }
                        onChange={dates => {
                            setFilters(current => ({
                                ...current,
                                modifiedDateFrom:
                                    dates?.[0]?.format('YYYY-MM-DD') || null,
                                modifiedDateTo:
                                    dates?.[1]?.format('YYYY-MM-DD') || null
                            }));
                        }}
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Вакансия</span>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Все вакансии"
                        value={vacancyFilter || undefined}
                        style={{width: '100%'}}
                        options={vacancyOptions}
                        onChange={value => setVacancyFilter(value || '')}
                    />
                </div>
                {!isSelfOnlyHr ? (
                    <div className="ba-filter-field">
                        <span className="ba-filter-label">Ответственный HR</span>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Все HR"
                            value={filters.user}
                            style={{width: '100%'}}
                            options={users.map(item => ({
                                value: item.id,
                                label: item.name
                            }))}
                            onChange={value =>
                                setFilters(current => ({...current, user: value}))
                            }
                        />
                    </div>
                ) : (
                    <div className="ba-filter-field">
                        <span className="ba-filter-label">Сотрудник</span>
                        <Text>
                            {users[0]?.name ||
                                'Вы (только ваши сделки)'}
                        </Text>
                        {!analyticsScope.linked ? (
                            <Alert
                                type="warning"
                                showIcon
                                style={{marginTop: 8}}
                                message="Не привязан Bitrix user"
                                description="Обратитесь к администратору: в профиле нужен bitrix_user_id."
                            />
                        ) : null}
                    </div>
                )}
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Стадия</span>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Все стадии"
                        value={filters.stage}
                        style={{width: '100%'}}
                        options={stageOptions.map(item => ({
                            value: item.stage_id,
                            label: item.stage_name
                        }))}
                        onChange={value =>
                            setFilters(current => ({...current, stage: value}))
                        }
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Группа</span>
                    <Select
                        allowClear
                        placeholder="Все группы"
                        value={filters.semantic}
                        style={{width: '100%'}}
                        options={Object.entries(SEMANTIC_META).map(([value, meta]) => ({
                            value,
                            label: meta.name
                        }))}
                        onChange={value =>
                            setFilters(current => ({...current, semantic: value}))
                        }
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Источник</span>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Все источники"
                        value={filters.source}
                        style={{width: '100%'}}
                        options={sourceOptions.map(item => ({
                            value: item.id,
                            label: item.name
                        }))}
                        onChange={value =>
                            setFilters(current => ({...current, source: value}))
                        }
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Поиск</span>
                    <Input
                        allowClear
                        prefix={<SearchOutlined/>}
                        placeholder="ID, ФИО, стадия…"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                    />
                </div>
                <div className="ba-filter-field">
                    <span className="ba-filter-label">Телефон</span>
                    <Input
                        allowClear
                        prefix={<PhoneOutlined/>}
                        placeholder="+7…"
                        value={phoneFilter}
                        onChange={event => setPhoneFilter(event.target.value)}
                    />
                </div>
                <div className="ba-filter-field">
                    <Space>
                        <Switch
                            checked={hasRecordingOnly}
                            onChange={setHasRecordingOnly}
                        />
                        <Text>Только с записью</Text>
                    </Space>
                </div>
            </div>
            <div className="ba-filter__footer">
                <Button
                    type="primary"
                    icon={<ReloadOutlined/>}
                    loading={loading}
                    onClick={loadData}
                    block
                >
                    Применить
                </Button>
                <Button icon={<ClearOutlined/>} onClick={resetFilters}>
                    Сброс
                </Button>
            </div>
        </aside>
    );


    return (
        <ConfigProvider locale={ruRU}>
            <Layout fullHeight>
                <div className="ba-shell">
                    {filterVisible ? (
                        <>
                            <div
                                style={{
                                    flex: `0 0 ${paneLayout.filterPx}px`,
                                    width: paneLayout.filterPx,
                                    minWidth: paneLayout.filterPx,
                                    maxWidth: paneLayout.filterPx,
                                    height: '100%',
                                    overflow: 'hidden'
                                }}
                            >
                                {filterPanel}
                            </div>
                            <div
                                className="ba-pane-resizer"
                                title="Ширина фильтра"
                                onMouseDown={event => startPaneResize('filter', event)}
                            />
                        </>
                    ) : null}

                    <div className="ba-center ba-center--stats">
                        <div className="ba-center__toolbar">
                            <Space wrap>
                                {!filterVisible ? (
                                    <Button
                                        icon={<MenuUnfoldOutlined/>}
                                        onClick={() => setFilterVisiblePersist(true)}
                                    >
                                        Фильтр
                                    </Button>
                                ) : null}
                                <div>
                                    <Title level={4} style={{margin: 0}}>
                                        {isSelfOnlyHr
                                            ? 'Моя статистика'
                                            : 'Статистика HR'}
                                    </Title>
                                    <Text type="secondary">
                                        {filteredDeals.length.toLocaleString('ru-RU')} сделок
                                        {' · '}создание: {periodLabel}
                                        {modifiedPeriodLabel
                                            ? ` · изменение: ${modifiedPeriodLabel}`
                                            : ''}
                                        {isSelfOnlyHr ? ' · только ваши данные' : ''}
                                    </Text>
                                </div>
                            </Space>
                            <Space wrap>
                                {analyticsScope.can_manage_vacancies ? (
                                    <Button
                                        icon={<AppstoreOutlined/>}
                                        onClick={openVacancyPanel}
                                    >
                                        Вакансии
                                    </Button>
                                ) : null}
                                {!dealsVisible ? (
                                    <Button
                                        icon={<UnorderedListOutlined/>}
                                        onClick={() => setDealsVisiblePersist(true)}
                                    >
                                        Сделки
                                    </Button>
                                ) : null}
                                <Button
                                    icon={<DownloadOutlined/>}
                                    disabled={!filteredDeals.length}
                                    onClick={exportExcel}
                                >
                                    Excel
                                </Button>
                                <Button
                                    icon={<ReloadOutlined/>}
                                    loading={loading}
                                    onClick={loadData}
                                >
                                    Обновить
                                </Button>
                            </Space>
                        </div>

                        {error ? (
                            <Alert
                                type="warning"
                                showIcon
                                closable
                                message={error}
                                style={{marginBottom: 10}}
                            />
                        ) : null}

                        <Spin spinning={loading} wrapperClassName="ba-board-spin">
                            <div className="ba-stats ba-stats--main">
                                <div className="ba-stats__body">
                                    <Title level={5} className="ba-stats__section-title">
                                        Общая статистика за период
                                    </Title>
                                    <Text type="secondary" className="ba-stats__section-hint">
                                        Воронка по истории стадий · создание: {periodLabel}
                                        {modifiedPeriodLabel
                                            ? ` · изменение: ${modifiedPeriodLabel}`
                                            : ''}
                                        {periodFunnelSummary.loaded
                                            ? ` · входящие ${periodFunnelSummary.incoming}`
                                            : ''}
                                    </Text>
                                    <SummaryKpiGrid
                                        summary={periodFunnelSummary}
                                        funnelStageHint={periodFunnelSummary.loaded}
                                    />
                                    {periodFunnelSummary.stageBreakdown.length ? (
                                        <div className="ba-period-funnel-stages">
                                            <div className="ba-vacancy-stages">
                                                {periodFunnelSummary.stageBreakdown.map(stage => (
                                                    <div
                                                        className="ba-vacancy-stages__row"
                                                        key={stage.name}
                                                        title={stage.name}
                                                    >
                                                        <span
                                                            className="ba-vacancy-stages__dot"
                                                            style={{background: stage.color}}
                                                        />
                                                        <span className="ba-vacancy-stages__name">
                                                            {stage.short}
                                                        </span>
                                                        <span className="ba-vacancy-stages__count">
                                                            {stage.count}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}

                                    {!isSelfOnlyHr ? (
                                        <>
                                    <Title level={5} className="ba-stats__section-title">
                                        <TeamOutlined/> По каждому HR
                                    </Title>
                                    <Text type="secondary" style={{display: 'block', marginBottom: 8, fontSize: 12}}>
                                        Звонки · перемещения · % выхода · клик — полная статистика
                                    </Text>
                                    {recruiterComputed.length ? (
                                        <div className="ba-stats__hr-grid">
                                            {recruiterComputed.map(item => (
                                                <div
                                                    className="ba-stats__hr-card ba-stats__hr-card--clickable"
                                                    key={item.assigned_by_id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        openHrSheet(item.assigned_by_id)
                                                    }
                                                    onKeyDown={event => {
                                                        if (
                                                            event.key === 'Enter' ||
                                                            event.key === ' '
                                                        ) {
                                                            event.preventDefault();
                                                            openHrSheet(item.assigned_by_id);
                                                        }
                                                    }}
                                                >
                                                    <div className="ba-stats__hr-name">
                                                        {item.user_name}
                                                    </div>
                                                    <div className="ba-stats__hr-meta">
                                                        {item.total} сделок · найм {item.success} · выход {item.failed}
                                                    </div>
                                                    <div className="ba-stats__hr-meta">
                                                        <PhoneOutlined/> {item.calls_count} зв.
                                                        {item.recorded_count
                                                            ? ` · запись ${item.recorded_count}`
                                                            : ''}
                                                        {' · '}
                                                        перемещ. {item.movements_count}
                                                    </div>
                                                    <div className="ba-stats__hr-card-foot">
                                                        <span>
                                                            выход{' '}
                                                            <Text strong style={{color: '#ff4d4f'}}>
                                                                {item.exit_rate}%
                                                            </Text>
                                                        </span>
                                                        <span>
                                                            ср. жизнь {item.avg_lifetime_days} дн.
                                                        </span>
                                                        <span>конв. {item.conversion}%</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Нет данных по HR"
                                        />
                                    )}
                                        </>
                                    ) : null}

                                    <Title level={5} className="ba-stats__section-title">
                                        <AppstoreOutlined/> По вакансиям
                                    </Title>
                                    <Text type="secondary" className="ba-stats__section-hint">
                                        Только активные · воронка по истории стадий · клик — деталка
                                    </Text>
                                    {vacancyComputed.length ? (
                                        <div className="ba-stats__hr-grid ba-stats__vacancy-grid">
                                            {vacancyComputed.map(item => (
                                                <div
                                                    className="ba-stats__hr-card ba-stats__vacancy-card"
                                                    key={item.id || item.name}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        if (item.id) {
                                                            openVacancyPanel();
                                                            loadVacancyDetail(item.id);
                                                        } else {
                                                            openVacancyByName(item.name);
                                                        }
                                                    }}
                                                    onKeyDown={event => {
                                                        if (
                                                            event.key === 'Enter' ||
                                                            event.key === ' '
                                                        ) {
                                                            event.preventDefault();
                                                            if (item.id) {
                                                                openVacancyPanel();
                                                                loadVacancyDetail(item.id);
                                                            } else {
                                                                openVacancyByName(item.name);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <div className="ba-stats__hr-name">
                                                        {item.name}
                                                    </div>
                                                    <div className="ba-stats__hr-meta">
                                                        {item.total} заявок · входящие{' '}
                                                        <Text strong>{item.incoming}</Text>
                                                        {' · '}найм {item.success} · выход{' '}
                                                        {item.failed}
                                                    </div>
                                                    <div className="ba-stats__hr-meta">
                                                        <PhoneOutlined/> {item.calls_count} зв.
                                                        {item.recorded_count
                                                            ? ` · запись ${item.recorded_count}`
                                                            : ''}
                                                        {item.talk_duration_sec
                                                            ? ` · ${formatDurationSec(item.talk_duration_sec)}`
                                                            : ''}
                                                    </div>
                                                    <div className="ba-vacancy-stages">
                                                        {item.stageBreakdown.length ? (
                                                            item.stageBreakdown.map(stage => (
                                                                <div
                                                                    className="ba-vacancy-stages__row"
                                                                    key={stage.name}
                                                                    title={stage.name}
                                                                >
                                                                    <span
                                                                        className="ba-vacancy-stages__dot"
                                                                        style={{
                                                                            background: stage.color
                                                                        }}
                                                                    />
                                                                    <span className="ba-vacancy-stages__name">
                                                                        {stage.short}
                                                                    </span>
                                                                    <span className="ba-vacancy-stages__count">
                                                                        {stage.count}
                                                                    </span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <Text type="secondary" style={{fontSize: 12}}>
                                                                Нет разбивки по стадиям
                                                            </Text>
                                                        )}
                                                    </div>
                                                    <div className="ba-stats__hr-card-foot">
                                                        <span>
                                                            выход{' '}
                                                            <Text
                                                                strong
                                                                style={{color: '#ff4d4f'}}
                                                            >
                                                                {item.exit_rate}%
                                                            </Text>
                                                        </span>
                                                        <span>
                                                            ср. жизнь {item.avg_lifetime_days} дн.
                                                        </span>
                                                        <span>конв. {item.conversion}%</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Нет данных по активным вакансиям"
                                        />
                                    )}

                                    <Title level={5} className="ba-stats__section-title">
                                        Конверсия по вакансиям
                                    </Title>
                                    <Text type="secondary" className="ba-stats__section-hint">
                                        Отклики · расходы · средняя стоимость отклика · разворот по дням
                                    </Text>
                                    {vacancyConversion.length ? (
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            pagination={false}
                                            scroll={{x: 900}}
                                            dataSource={vacancyConversion}
                                            expandable={{
                                                expandedRowRender: record =>
                                                    toArray(record.by_day).length ? (
                                                        <Table
                                                            size="small"
                                                            pagination={false}
                                                            rowKey="day"
                                                            dataSource={record.by_day}
                                                            columns={[
                                                                {
                                                                    title: 'День',
                                                                    dataIndex: 'day',
                                                                    width: 120,
                                                                    render: day =>
                                                                        day
                                                                            ? dayjs(day).format(
                                                                                  'DD.MM.YYYY'
                                                                              )
                                                                            : '—'
                                                                },
                                                                {
                                                                    title: 'Отклики',
                                                                    dataIndex: 'responses',
                                                                    width: 90,
                                                                    align: 'right'
                                                                },
                                                                {
                                                                    title: 'Деньги',
                                                                    dataIndex: 'spend',
                                                                    width: 120,
                                                                    align: 'right',
                                                                    render: formatMoney
                                                                },
                                                                {
                                                                    title: 'Ср. стоимость отклика',
                                                                    dataIndex:
                                                                        'avg_cost_per_response',
                                                                    align: 'right',
                                                                    render: formatMoney
                                                                }
                                                            ]}
                                                        />
                                                    ) : (
                                                        <Text type="secondary">
                                                            Нет разбивки по дням
                                                        </Text>
                                                    )
                                            }}
                                            columns={[
                                                {
                                                    title: 'Вакансия',
                                                    dataIndex: 'name',
                                                    ellipsis: true,
                                                    render: (name, row) => (
                                                        <Button
                                                            type="link"
                                                            style={{padding: 0, height: 'auto'}}
                                                            onClick={() => {
                                                                if (!row.id) return;
                                                                openVacancyPanel();
                                                                loadVacancyDetail(row.id);
                                                            }}
                                                        >
                                                            {name}
                                                        </Button>
                                                    )
                                                },
                                                {
                                                    title: 'Отклики',
                                                    dataIndex: 'responses',
                                                    width: 90,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Найм',
                                                    dataIndex: 'success',
                                                    width: 80,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Конв. %',
                                                    dataIndex: 'conversion',
                                                    width: 90,
                                                    align: 'right',
                                                    render: v => `${v}%`
                                                },
                                                {
                                                    title: 'Деньги',
                                                    dataIndex: 'spend',
                                                    width: 120,
                                                    align: 'right',
                                                    render: formatMoney
                                                },
                                                {
                                                    title: 'Ср. стоимость отклика',
                                                    dataIndex: 'avg_cost_per_response',
                                                    width: 160,
                                                    align: 'right',
                                                    render: formatMoney
                                                }
                                            ]}
                                        />
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Нет данных по конверсии вакансий"
                                        />
                                    )}

                                    <Title level={5} className="ba-stats__section-title">
                                        Без движения 3+ дня
                                    </Title>
                                    <Text type="secondary" className="ba-stats__section-hint">
                                        Сделки в работе · последнее перемещение по стадии более 3 дней назад · клик — открыть
                                    </Text>
                                    {staleDeals.length ? (
                                        <Table
                                            size="small"
                                            rowKey="id"
                                            pagination={{
                                                pageSize: 15,
                                                size: 'small',
                                                showSizeChanger: false,
                                                hideOnSinglePage: true
                                            }}
                                            scroll={{x: 920}}
                                            dataSource={staleDeals}
                                            onRow={row => ({
                                                onClick: () =>
                                                    openDealDetails({id: row.id}),
                                                style: {cursor: 'pointer'}
                                            })}
                                            columns={[
                                                {
                                                    title: 'Сделка',
                                                    dataIndex: 'title',
                                                    ellipsis: true,
                                                    render: (title, row) => (
                                                        <Space direction="vertical" size={0}>
                                                            <Text ellipsis style={{maxWidth: 260}}>
                                                                {title || `Сделка #${row.id}`}
                                                            </Text>
                                                            <Text type="secondary" style={{fontSize: 11}}>
                                                                #{row.id}
                                                            </Text>
                                                        </Space>
                                                    )
                                                },
                                                {
                                                    title: 'HR',
                                                    dataIndex: 'user_name',
                                                    width: 120,
                                                    ellipsis: true
                                                },
                                                {
                                                    title: 'Вакансия',
                                                    dataIndex: 'vacancy_name',
                                                    width: 140,
                                                    ellipsis: true,
                                                    render: v => v || '—'
                                                },
                                                {
                                                    title: 'Стадия',
                                                    dataIndex: 'stage_name',
                                                    width: 140,
                                                    ellipsis: true,
                                                    render: (name, row) => {
                                                        const meta =
                                                            STAGE_META[name] ||
                                                            SEMANTIC_META[
                                                                getSemanticId(row)
                                                            ] ||
                                                            {};
                                                        return (
                                                            <Tag
                                                                color={meta.tag || 'default'}
                                                                style={{margin: 0}}
                                                            >
                                                                {name}
                                                            </Tag>
                                                        );
                                                    }
                                                },
                                                {
                                                    title: 'Без движ.',
                                                    dataIndex: 'idle_days',
                                                    width: 90,
                                                    align: 'right',
                                                    render: (days, row) => (
                                                        <Text
                                                            strong
                                                            style={{
                                                                color:
                                                                    days >= 7
                                                                        ? '#ff4d4f'
                                                                        : '#fa8c16'
                                                            }}
                                                        >
                                                            {days ?? '—'} дн.
                                                        </Text>
                                                    )
                                                },
                                                {
                                                    title: 'Последнее движение',
                                                    dataIndex: 'last_movement_at',
                                                    width: 130,
                                                    render: v =>
                                                        v
                                                            ? dayjs(v).format(
                                                                  'DD.MM.YYYY HH:mm'
                                                              )
                                                            : '—'
                                                },
                                                {
                                                    title: 'Телефон',
                                                    dataIndex: 'contact_phone',
                                                    width: 120,
                                                    ellipsis: true,
                                                    render: v => v || '—'
                                                }
                                            ]}
                                        />
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Нет сделок без движения более 3 дней"
                                        />
                                    )}

                                    <Title level={5} className="ba-stats__section-title">
                                        <BarChartOutlined/> Графики и конверсия
                                    </Title>
                                    <Text
                                        type="secondary"
                                        style={{
                                            display: 'block',
                                            marginBottom: 12,
                                            fontSize: 12
                                        }}
                                    >
                                        По текущим фильтрам · {filteredDeals.length.toLocaleString('ru-RU')} сделок
                                    </Text>

                                    {filteredDeals.length ? (
                                        <div className="ba-charts">
                                            <div className="ba-charts__card">
                                                <div className="ba-charts__title">Состав воронки</div>
                                                <div className="ba-charts__body ba-charts__body--sm">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={semanticChartData}
                                                                dataKey="value"
                                                                nameKey="name"
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius={48}
                                                                outerRadius={78}
                                                                paddingAngle={2}
                                                            >
                                                                {semanticChartData.map(item => (
                                                                    <Cell
                                                                        key={item.key}
                                                                        fill={item.color}
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <RechartsTooltip
                                                                formatter={(value, name) => [
                                                                    value,
                                                                    name
                                                                ]}
                                                            />
                                                            <Legend />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <div className="ba-charts__foot">
                                                    конв. {periodFunnelSummary.conversion}% · найм{' '}
                                                    {periodFunnelSummary.hire_rate}% · выход{' '}
                                                    {periodFunnelSummary.exit_rate}%
                                                </div>
                                            </div>

                                            <div className="ba-charts__card ba-charts__card--wide">
                                                <div className="ba-charts__title">Заявки по стадиям</div>
                                                <div className="ba-charts__body">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart
                                                            data={stageFunnelData}
                                                            margin={{top: 8, right: 8, left: 0, bottom: 8}}
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 3"
                                                                vertical={false}
                                                            />
                                                            <XAxis
                                                                dataKey="short"
                                                                tick={{fontSize: 11}}
                                                                interval={0}
                                                                angle={-25}
                                                                textAnchor="end"
                                                                height={56}
                                                            />
                                                            <YAxis
                                                                allowDecimals={false}
                                                                tick={{fontSize: 11}}
                                                                width={36}
                                                            />
                                                            <RechartsTooltip
                                                                labelFormatter={(_, payload) =>
                                                                    payload?.[0]?.payload?.name || ''
                                                                }
                                                            />
                                                            <Bar
                                                                dataKey="count"
                                                                name="Заявки"
                                                                radius={[6, 6, 0, 0]}
                                                            >
                                                                {stageFunnelData.map(item => (
                                                                    <Cell
                                                                        key={item.name}
                                                                        fill={item.color}
                                                                    />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="ba-charts__card ba-charts__card--full">
                                                <div className="ba-charts__title">
                                                    Сколько заявок пришло по дням
                                                </div>
                                                <Text
                                                    type="secondary"
                                                    className="ba-charts__hint"
                                                >
                                                    Столбцы — число новых заявок за день (по дате создания),
                                                    цвет — куда они дошли сейчас: в работе / найм / выход.
                                                    Линия — доля найма среди заявок этого дня
                                                    (найм ÷ все заявки дня).
                                                </Text>
                                                <div className="ba-charts__body ba-charts__body--lg">
                                                    {dailyTrendData.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <ComposedChart
                                                                data={dailyTrendData}
                                                                margin={{
                                                                    top: 8,
                                                                    right: 12,
                                                                    left: 0,
                                                                    bottom: 8
                                                                }}
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 3"
                                                                    vertical={false}
                                                                />
                                                                <XAxis
                                                                    dataKey="label"
                                                                    tick={{fontSize: 11}}
                                                                    minTickGap={18}
                                                                />
                                                                <YAxis
                                                                    yAxisId="left"
                                                                    allowDecimals={false}
                                                                    tick={{fontSize: 11}}
                                                                    width={36}
                                                                    label={{
                                                                        value: 'заявки',
                                                                        angle: -90,
                                                                        position: 'insideLeft',
                                                                        style: {
                                                                            fontSize: 11,
                                                                            fill: '#8c8c8c'
                                                                        }
                                                                    }}
                                                                />
                                                                <YAxis
                                                                    yAxisId="right"
                                                                    orientation="right"
                                                                    domain={[0, 100]}
                                                                    tick={{fontSize: 11}}
                                                                    width={42}
                                                                    unit="%"
                                                                />
                                                                <RechartsTooltip
                                                                    formatter={(value, name) => {
                                                                        if (
                                                                            name ===
                                                                            '% найма за день'
                                                                        ) {
                                                                            return [
                                                                                `${value}%`,
                                                                                name
                                                                            ];
                                                                        }
                                                                        return [
                                                                            `${value} шт.`,
                                                                            name
                                                                        ];
                                                                    }}
                                                                    labelFormatter={(_, payload) => {
                                                                        const row =
                                                                            payload?.[0]?.payload;
                                                                        if (!row?.day) return '';
                                                                        return `${dayjs(row.day).format(
                                                                            'DD MMM YYYY'
                                                                        )} · всего ${row.total}`;
                                                                    }}
                                                                />
                                                                <Legend />
                                                                <Bar
                                                                    yAxisId="left"
                                                                    dataKey="work"
                                                                    name="Ещё в работе"
                                                                    stackId="a"
                                                                    fill={CHART_COLORS.work}
                                                                />
                                                                <Bar
                                                                    yAxisId="left"
                                                                    dataKey="success"
                                                                    name="Уже найм"
                                                                    stackId="a"
                                                                    fill={CHART_COLORS.success}
                                                                />
                                                                <Bar
                                                                    yAxisId="left"
                                                                    dataKey="failed"
                                                                    name="Уже выход"
                                                                    stackId="a"
                                                                    fill={CHART_COLORS.failed}
                                                                    radius={[4, 4, 0, 0]}
                                                                />
                                                                <Line
                                                                    yAxisId="right"
                                                                    type="monotone"
                                                                    dataKey="conversion"
                                                                    name="% найма за день"
                                                                    stroke={CHART_COLORS.conversion}
                                                                    strokeWidth={2}
                                                                    dot={{r: 3}}
                                                                    activeDot={{r: 5}}
                                                                />
                                                            </ComposedChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            description="Нет дат создания"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            {!isSelfOnlyHr ? (
                                            <div className="ba-charts__card">
                                                <div className="ba-charts__title">
                                                    Конверсия по HR
                                                </div>
                                                <div className="ba-charts__body">
                                                    {hrConversionChartData.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart
                                                                layout="vertical"
                                                                data={hrConversionChartData}
                                                                margin={{
                                                                    top: 4,
                                                                    right: 16,
                                                                    left: 4,
                                                                    bottom: 4
                                                                }}
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 3"
                                                                    horizontal={false}
                                                                />
                                                                <XAxis
                                                                    type="number"
                                                                    domain={[0, 100]}
                                                                    tick={{fontSize: 11}}
                                                                    unit="%"
                                                                />
                                                                <YAxis
                                                                    type="category"
                                                                    dataKey="name"
                                                                    width={96}
                                                                    tick={{fontSize: 11}}
                                                                />
                                                                <RechartsTooltip
                                                                    formatter={(value, name) =>
                                                                        name === 'Конверсия'
                                                                            ? [`${value}%`, name]
                                                                            : [value, name]
                                                                    }
                                                                    labelFormatter={(_, payload) =>
                                                                        payload?.[0]?.payload
                                                                            ?.fullName || ''
                                                                    }
                                                                />
                                                                <Bar
                                                                    dataKey="conversion"
                                                                    name="Конверсия"
                                                                    fill={CHART_COLORS.conversion}
                                                                    radius={[0, 6, 6, 0]}
                                                                />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            description="Нет данных по HR"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            ) : null}

                                            <div className="ba-charts__card">
                                                <div className="ba-charts__title">
                                                    Конверсия по вакансиям
                                                </div>
                                                <div className="ba-charts__body">
                                                    {vacancyConversionChartData.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart
                                                                layout="vertical"
                                                                data={vacancyConversionChartData}
                                                                margin={{
                                                                    top: 4,
                                                                    right: 16,
                                                                    left: 4,
                                                                    bottom: 4
                                                                }}
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 3"
                                                                    horizontal={false}
                                                                />
                                                                <XAxis
                                                                    type="number"
                                                                    domain={[0, 100]}
                                                                    tick={{fontSize: 11}}
                                                                    unit="%"
                                                                />
                                                                <YAxis
                                                                    type="category"
                                                                    dataKey="name"
                                                                    width={96}
                                                                    tick={{fontSize: 11}}
                                                                />
                                                                <RechartsTooltip
                                                                    formatter={(value, name) =>
                                                                        name === 'Конверсия'
                                                                            ? [`${value}%`, name]
                                                                            : [value, name]
                                                                    }
                                                                    labelFormatter={(_, payload) =>
                                                                        payload?.[0]?.payload
                                                                            ?.fullName || ''
                                                                    }
                                                                />
                                                                <Bar
                                                                    dataKey="conversion"
                                                                    name="Конверсия"
                                                                    fill={CHART_COLORS.total}
                                                                    radius={[0, 6, 6, 0]}
                                                                />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            description="Нет данных по вакансиям"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Нет данных для графиков"
                                        />
                                    )}
                                </div>
                            </div>
                        </Spin>

                        <div
                            className={`ba-hr-sheet${hrSheetOpen ? ' is-open' : ''}`}
                            aria-hidden={!hrSheetOpen}
                        >
                            <div className="ba-deal-sheet__handle">
                                <div className="ba-deal-sheet__handle-bar"/>
                            </div>
                            <div className="ba-deal-sheet__header">
                                <div style={{minWidth: 0}}>
                                    <Text type="secondary" style={{fontSize: 12}}>
                                        Статистика HR
                                    </Text>
                                    <Title level={5} className="ba-deal-sheet__title" ellipsis>
                                        {hrDetail?.summary?.user_name || 'Сотрудник'}
                                    </Title>
                                </div>
                                <Button
                                    type="text"
                                    icon={<CloseOutlined/>}
                                    onClick={closeHrSheet}
                                />
                            </div>
                            <div className="ba-deal-sheet__body ba-hr-sheet__body">
                                {hrDetail ? (
                                    <>
                                        <div className="ba-stats__kpi ba-stats__kpi--wide ba-hr-sheet__kpi">
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Всего</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.total}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">В работе</span>
                                                <span className="ba-stats__kpi-value" style={{color: '#1677ff'}}>
                                                    {hrDetail.summary.work}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Найм</span>
                                                <span className="ba-stats__kpi-value" style={{color: '#52c41a'}}>
                                                    {hrDetail.summary.success}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Выход</span>
                                                <span className="ba-stats__kpi-value" style={{color: '#ff4d4f'}}>
                                                    {hrDetail.summary.failed}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Конверсия</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.conversion}%
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">% выхода</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.exit_rate}%
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Касания</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.movements_count}
                                                </span>
                                                <Text type="secondary" style={{fontSize: 11}}>
                                                    {hrDetail.summary.touches_per_deal} / сделку
                                                </Text>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Звонки</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.calls_count}
                                                </span>
                                                <Text type="secondary" style={{fontSize: 11}}>
                                                    {hrDetail.summary.calls_per_deal} / сделку
                                                </Text>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Разговоры</span>
                                                <span className="ba-stats__kpi-value" style={{fontSize: 16}}>
                                                    {formatDurationSec(
                                                        hrDetail.summary.talk_duration_sec
                                                    )}
                                                </span>
                                                <Text type="secondary" style={{fontSize: 11}}>
                                                    ср.{' '}
                                                    {formatDurationSec(
                                                        hrDetail.summary.avg_duration_sec
                                                    )}
                                                </Text>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Записи</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.recorded_count}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Ср. жизнь</span>
                                                <span className="ba-stats__kpi-value" style={{fontSize: 16}}>
                                                    {hrDetail.summary.avg_lifetime_days} дн.
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">Вакансий</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.vacancies_count}
                                                </span>
                                                <Text type="secondary" style={{fontSize: 11}}>
                                                    без вак. {hrDetail.summary.without_vacancy}
                                                </Text>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">С телефоном</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.with_phone}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">С записью</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.with_recording}
                                                </span>
                                            </div>
                                            <div className="ba-stats__kpi-item">
                                                <span className="ba-stats__kpi-label">% найма</span>
                                                <span className="ba-stats__kpi-value">
                                                    {hrDetail.summary.hire_rate}%
                                                </span>
                                            </div>
                                        </div>

                                        <div className="ba-charts ba-hr-sheet__charts">
                                            <div className="ba-charts__card">
                                                <div className="ba-charts__title">Состав сделок</div>
                                                <div className="ba-charts__body ba-charts__body--sm">
                                                    {hrDetail.semantic.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <PieChart>
                                                                <Pie
                                                                    data={hrDetail.semantic}
                                                                    dataKey="value"
                                                                    nameKey="name"
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    innerRadius={40}
                                                                    outerRadius={68}
                                                                    paddingAngle={2}
                                                                >
                                                                    {hrDetail.semantic.map(item => (
                                                                        <Cell
                                                                            key={item.key}
                                                                            fill={item.color}
                                                                        />
                                                                    ))}
                                                                </Pie>
                                                                <RechartsTooltip />
                                                                <Legend />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="ba-charts__card">
                                                <div className="ba-charts__title">По стадиям</div>
                                                <div className="ba-charts__body ba-charts__body--sm">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart
                                                            data={hrDetail.stageChart}
                                                            margin={{top: 4, right: 8, left: 0, bottom: 8}}
                                                        >
                                                            <CartesianGrid
                                                                strokeDasharray="3 3"
                                                                vertical={false}
                                                            />
                                                            <XAxis
                                                                dataKey="name"
                                                                tick={{fontSize: 10}}
                                                                interval={0}
                                                                angle={-20}
                                                                textAnchor="end"
                                                                height={48}
                                                            />
                                                            <YAxis
                                                                allowDecimals={false}
                                                                width={32}
                                                                tick={{fontSize: 10}}
                                                            />
                                                            <RechartsTooltip
                                                                labelFormatter={(_, payload) =>
                                                                    payload?.[0]?.payload?.fullName ||
                                                                    ''
                                                                }
                                                            />
                                                            <Bar dataKey="total" name="Заявки" radius={[4, 4, 0, 0]}>
                                                                {hrDetail.stageChart.map(item => (
                                                                    <Cell
                                                                        key={item.fullName}
                                                                        fill={item.color}
                                                                    />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="ba-charts__card ba-charts__card--full">
                                                <div className="ba-charts__title">
                                                    Заявки по дням и % найма
                                                </div>
                                                <div className="ba-charts__body">
                                                    {hrDetail.by_day.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <ComposedChart
                                                                data={hrDetail.by_day}
                                                                margin={{
                                                                    top: 4,
                                                                    right: 12,
                                                                    left: 0,
                                                                    bottom: 4
                                                                }}
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 3"
                                                                    vertical={false}
                                                                />
                                                                <XAxis
                                                                    dataKey="label"
                                                                    tick={{fontSize: 10}}
                                                                    minTickGap={16}
                                                                />
                                                                <YAxis
                                                                    yAxisId="left"
                                                                    allowDecimals={false}
                                                                    width={32}
                                                                    tick={{fontSize: 10}}
                                                                />
                                                                <YAxis
                                                                    yAxisId="right"
                                                                    orientation="right"
                                                                    domain={[0, 100]}
                                                                    width={36}
                                                                    unit="%"
                                                                    tick={{fontSize: 10}}
                                                                />
                                                                <RechartsTooltip />
                                                                <Legend />
                                                                <Bar
                                                                    yAxisId="left"
                                                                    dataKey="total"
                                                                    name="Заявки"
                                                                    fill={CHART_COLORS.total}
                                                                    radius={[4, 4, 0, 0]}
                                                                />
                                                                <Line
                                                                    yAxisId="right"
                                                                    type="monotone"
                                                                    dataKey="conversion"
                                                                    name="% найма"
                                                                    stroke={CHART_COLORS.conversion}
                                                                    strokeWidth={2}
                                                                    dot={false}
                                                                />
                                                            </ComposedChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="ba-charts__card ba-charts__card--full">
                                                <div className="ba-charts__title">
                                                    Конверсия по вакансиям
                                                </div>
                                                <div className="ba-charts__body">
                                                    {hrDetail.vacancyChart.length ? (
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart
                                                                layout="vertical"
                                                                data={hrDetail.vacancyChart}
                                                                margin={{
                                                                    top: 4,
                                                                    right: 16,
                                                                    left: 4,
                                                                    bottom: 4
                                                                }}
                                                            >
                                                                <CartesianGrid
                                                                    strokeDasharray="3 3"
                                                                    horizontal={false}
                                                                />
                                                                <XAxis
                                                                    type="number"
                                                                    domain={[0, 100]}
                                                                    unit="%"
                                                                    tick={{fontSize: 10}}
                                                                />
                                                                <YAxis
                                                                    type="category"
                                                                    dataKey="name"
                                                                    width={110}
                                                                    tick={{fontSize: 10}}
                                                                />
                                                                <RechartsTooltip
                                                                    formatter={(value, name) =>
                                                                        name === 'Конверсия'
                                                                            ? [`${value}%`, name]
                                                                            : [value, name]
                                                                    }
                                                                    labelFormatter={(_, payload) =>
                                                                        payload?.[0]?.payload
                                                                            ?.fullName || ''
                                                                    }
                                                                />
                                                                <Bar
                                                                    dataKey="conversion"
                                                                    name="Конверсия"
                                                                    fill={CHART_COLORS.conversion}
                                                                    radius={[0, 4, 4, 0]}
                                                                />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    ) : (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <Title level={5} style={{marginTop: 8}}>
                                            По вакансиям
                                        </Title>
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey="name"
                                            scroll={{x: 720}}
                                            dataSource={hrDetail.by_vacancy}
                                            columns={[
                                                {
                                                    title: 'Вакансия',
                                                    dataIndex: 'name',
                                                    key: 'name',
                                                    ellipsis: true
                                                },
                                                {
                                                    title: 'Всего',
                                                    dataIndex: 'total',
                                                    width: 70,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Найм',
                                                    dataIndex: 'success',
                                                    width: 70,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Выход',
                                                    dataIndex: 'failed',
                                                    width: 70,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Конв.',
                                                    dataIndex: 'conversion',
                                                    width: 70,
                                                    align: 'right',
                                                    render: v => `${v}%`
                                                },
                                                {
                                                    title: 'Звонки',
                                                    dataIndex: 'calls_count',
                                                    width: 80,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Разговоры',
                                                    dataIndex: 'talk_duration_sec',
                                                    width: 100,
                                                    align: 'right',
                                                    render: v => formatDurationSec(v)
                                                },
                                                {
                                                    title: 'Записи',
                                                    dataIndex: 'recorded_count',
                                                    width: 80,
                                                    align: 'right'
                                                }
                                            ]}
                                        />

                                        <Title level={5} style={{marginTop: 16}}>
                                            По источникам
                                        </Title>
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey="name"
                                            dataSource={hrDetail.by_source}
                                            columns={[
                                                {
                                                    title: 'Источник',
                                                    dataIndex: 'name',
                                                    ellipsis: true
                                                },
                                                {
                                                    title: 'Всего',
                                                    dataIndex: 'total',
                                                    width: 80,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Найм',
                                                    dataIndex: 'success',
                                                    width: 80,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Выход',
                                                    dataIndex: 'failed',
                                                    width: 80,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Конв.',
                                                    dataIndex: 'conversion',
                                                    width: 80,
                                                    align: 'right',
                                                    render: v => `${v}%`
                                                }
                                            ]}
                                        />

                                        <Title level={5} style={{marginTop: 16}}>
                                            <PhoneOutlined/> Звонки
                                            {hrCalls.length ? ` (${hrCalls.length})` : ''}
                                        </Title>
                                        <Text
                                            type="secondary"
                                            style={{display: 'block', marginBottom: 8, fontSize: 12}}
                                        >
                                            Из локальной базы АТС · по телефонам сделок HR в фильтре
                                        </Text>
                                        {hrCallsError ? (
                                            <Alert
                                                type="warning"
                                                showIcon
                                                message={hrCallsError}
                                                style={{marginBottom: 8}}
                                            />
                                        ) : null}
                                        {activeAudioName && audioUrls[activeAudioName] ? (
                                            <div
                                                className="ba-hr-audio-player"
                                                style={{
                                                    marginBottom: 10,
                                                    padding: '8px 10px',
                                                    background: '#fafafa',
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 10
                                                }}
                                            >
                                                <Space
                                                    style={{width: '100%', justifyContent: 'space-between'}}
                                                    wrap
                                                >
                                                    <Text type="secondary" style={{fontSize: 12}}>
                                                        Запись
                                                    </Text>
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        onClick={clearActiveAudio}
                                                    >
                                                        Закрыть
                                                    </Button>
                                                </Space>
                                                <audio
                                                    key={activeAudioName}
                                                    controls
                                                    autoPlay
                                                    preload="metadata"
                                                    src={audioUrls[activeAudioName]}
                                                    style={{width: '100%', height: 36, marginTop: 4}}
                                                />
                                            </div>
                                        ) : null}
                                        <Spin spinning={hrCallsLoading}>
                                            <Table
                                                size="small"
                                                pagination={{
                                                    pageSize: 20,
                                                    size: 'small',
                                                    showSizeChanger: false,
                                                    hideOnSinglePage: true
                                                }}
                                                rowKey="row_key"
                                                scroll={{x: 780, y: 320}}
                                                dataSource={hrCalls}
                                                locale={{emptyText: 'Звонков нет'}}
                                                columns={[
                                                    {
                                                        title: 'Дата',
                                                        width: 110,
                                                        render: (_, row) => {
                                                            const raw =
                                                                row.date ||
                                                                row.start ||
                                                                row.start_time ||
                                                                row.call_date;
                                                            return raw
                                                                ? dayjs(raw).format('DD.MM HH:mm')
                                                                : '—';
                                                        }
                                                    },
                                                    {
                                                        title: 'Сделка',
                                                        dataIndex: 'deal_title',
                                                        ellipsis: true,
                                                        render: (title, row) =>
                                                            title ? (
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    style={{
                                                                        padding: 0,
                                                                        height: 'auto'
                                                                    }}
                                                                    onClick={() => {
                                                                        if (!row.deal_id) return;
                                                                        closeHrSheet();
                                                                        openDealDetails({
                                                                            id: row.deal_id
                                                                        });
                                                                    }}
                                                                >
                                                                    {title}
                                                                </Button>
                                                            ) : (
                                                                <Text type="secondary">—</Text>
                                                            )
                                                    },
                                                    {
                                                        title: 'Тел.',
                                                        width: 120,
                                                        ellipsis: true,
                                                        render: (_, row) =>
                                                            row.matched_phone ||
                                                            row.callee_number ||
                                                            row.caller_number ||
                                                            '—'
                                                    },
                                                    {
                                                        title: 'Сек.',
                                                        width: 55,
                                                        align: 'right',
                                                        render: (_, row) =>
                                                            toNumber(
                                                                row.conversation_duration ||
                                                                    row.call_duration ||
                                                                    row.duration
                                                            ) || '—'
                                                    },
                                                    {
                                                        title: 'Запись',
                                                        width: 110,
                                                        render: (_, row) => {
                                                            const filename =
                                                                row.record_file_name;
                                                            if (
                                                                !filename &&
                                                                !row.has_recording
                                                            ) {
                                                                return (
                                                                    <Text type="secondary">
                                                                        нет
                                                                    </Text>
                                                                );
                                                            }
                                                            if (!filename) return '—';
                                                            const isActive =
                                                                activeAudioName === filename;
                                                            const isLoading =
                                                                audioLoadingName === filename;
                                                            return (
                                                                <Button
                                                                    size="small"
                                                                    type={
                                                                        isActive
                                                                            ? 'primary'
                                                                            : 'default'
                                                                    }
                                                                    loading={isLoading}
                                                                    icon={<SoundOutlined/>}
                                                                    onClick={async () => {
                                                                        try {
                                                                            await loadRecordingAudio(
                                                                                filename
                                                                            );
                                                                        } catch (audioError) {
                                                                            message.error(
                                                                                audioError
                                                                                    .response
                                                                                    ?.data
                                                                                    ?.error ||
                                                                                    'Не удалось загрузить запись'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    {isActive
                                                                        ? 'Играет'
                                                                        : 'Слушать'}
                                                                </Button>
                                                            );
                                                        }
                                                    }
                                                ]}
                                            />
                                        </Spin>

                                        <Title level={5} style={{marginTop: 16}}>
                                            Последние сделки
                                        </Title>
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey="id"
                                            scroll={{x: 900}}
                                            dataSource={hrDetail.recentDeals}
                                            onRow={row => ({
                                                onClick: () => {
                                                    closeHrSheet();
                                                    openDealDetails({id: row.id});
                                                },
                                                style: {cursor: 'pointer'}
                                            })}
                                            columns={[
                                                {
                                                    title: 'Сделка',
                                                    dataIndex: 'title',
                                                    ellipsis: true,
                                                    render: (title, row) => (
                                                        <Space direction="vertical" size={0}>
                                                            <Text ellipsis style={{maxWidth: 220}}>
                                                                {title}
                                                            </Text>
                                                            <Text type="secondary" style={{fontSize: 11}}>
                                                                #{row.id}
                                                            </Text>
                                                        </Space>
                                                    )
                                                },
                                                {
                                                    title: 'Вакансия',
                                                    dataIndex: 'vacancy_name',
                                                    ellipsis: true,
                                                    width: 160
                                                },
                                                {
                                                    title: 'Стадия',
                                                    dataIndex: 'stage_name',
                                                    ellipsis: true,
                                                    width: 140,
                                                    render: (name, row) => (
                                                        <Tag
                                                            color={
                                                                (
                                                                    SEMANTIC_META[row.semantic] ||
                                                                    {}
                                                                ).tag || 'default'
                                                            }
                                                        >
                                                            {name}
                                                        </Tag>
                                                    )
                                                },
                                                {
                                                    title: 'Звонки',
                                                    dataIndex: 'calls_count',
                                                    width: 70,
                                                    align: 'right'
                                                },
                                                {
                                                    title: 'Разговор',
                                                    dataIndex: 'talk_duration_sec',
                                                    width: 90,
                                                    align: 'right',
                                                    render: v => formatDurationSec(v)
                                                },
                                                {
                                                    title: 'Дней',
                                                    dataIndex: 'lifetime_days',
                                                    width: 70,
                                                    align: 'right',
                                                    render: v => (v == null ? '—' : v)
                                                },
                                                {
                                                    title: 'Создана',
                                                    dataIndex: 'date_create',
                                                    width: 110,
                                                    render: v => formatDate(v)
                                                }
                                            ]}
                                        />
                                    </>
                                ) : (
                                    <Empty description="Нет данных по сотруднику"/>
                                )}
                            </div>
                        </div>
                    </div>

                    {dealsVisible ? (
                        <>
                            <div
                                className="ba-pane-resizer"
                                title="Ширина сделок"
                                onMouseDown={event => startPaneResize('deals', event)}
                            />
                            <div
                                className="ba-deals"
                                style={{
                                    flex: `0 0 ${paneLayout.dealsPx}px`,
                                    width: paneLayout.dealsPx,
                                    minWidth: paneLayout.dealsPx,
                                    maxWidth: paneLayout.dealsPx
                                }}
                            >
                                <div className="ba-deals__header">
                                    <div>
                                        <Text type="secondary" style={{fontSize: 11}}>
                                            Справочно
                                        </Text>
                                        <Title level={5} style={{margin: 0}}>
                                            Сделки
                                            <Tag color="blue" style={{marginLeft: 8}}>
                                                {filteredDeals.length}
                                            </Tag>
                                        </Title>
                                    </div>
                                    <Space size={6} wrap>
                                        <Segmented
                                            size="small"
                                            value={boardView}
                                            onChange={setBoardViewPersist}
                                            options={[
                                                {
                                                    value: 'cards',
                                                    icon: <UnorderedListOutlined/>
                                                },
                                                {
                                                    value: 'kanban',
                                                    icon: <AppstoreOutlined/>
                                                }
                                            ]}
                                        />
                                        <Button
                                            type="text"
                                            icon={<MenuUnfoldOutlined style={{transform: 'scaleX(-1)'}}/>}
                                            onClick={() => setDealsVisiblePersist(false)}
                                            title="Скрыть сделки"
                                        />
                                    </Space>
                                </div>

                                <div
                                    className={`ba-deals__board${
                                        dealOpen ? ' is-sheet-open' : ''
                                    }`}
                                >
                                    <Spin
                                        spinning={loading}
                                        wrapperClassName="ba-board-spin"
                                    >
                                        {!filteredDeals.length && !loading ? (
                                            <Empty description="Сделки по фильтрам не найдены"/>
                                        ) : boardView === 'kanban' ? (
                                            <div className="ba-kanban">
                                                {kanbanColumns.map(column => (
                                                    <div
                                                        key={column.id}
                                                        className="ba-kanban__column"
                                                        style={{borderTopColor: column.color}}
                                                    >
                                                        <div className="ba-kanban__column-head">
                                                            <Text strong ellipsis>
                                                                {column.name}
                                                            </Text>
                                                            <Tag color={column.color}>
                                                                {column.deals.length}
                                                            </Tag>
                                                        </div>
                                                        <div className="ba-kanban__column-body">
                                                            {column.deals.map(renderDealCard)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="ba-cards-list">
                                                {filteredDeals.map(renderDealCard)}
                                            </div>
                                        )}
                                    </Spin>
                                </div>

                                <div
                                    className={`ba-deal-sheet${dealOpen ? ' is-open' : ''}`}
                                    aria-hidden={!dealOpen}
                                >
                                    <div className="ba-deal-sheet__handle">
                                        <div className="ba-deal-sheet__handle-bar"/>
                                    </div>
                                    <div className="ba-deal-sheet__header">
                                        <div style={{minWidth: 0}}>
                                            <Text type="secondary" style={{fontSize: 12}}>
                                                Сделка
                                                {currentDeal?.id ? ` #${currentDeal.id}` : ''}
                                            </Text>
                                            <Title level={5} className="ba-deal-sheet__title" ellipsis>
                                                {currentDeal?.title || 'Загрузка…'}
                                            </Title>
                                        </div>
                                        <Space wrap size={6}>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<SaveOutlined/>}
                                                loading={savingDeal}
                                                disabled={!currentDeal?.id || dealLoading}
                                                onClick={saveDeal}
                                            >
                                                Сохранить
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<CloseOutlined/>}
                                                onClick={closeDealSheet}
                                            >
                                                Закрыть
                                            </Button>
                                        </Space>
                                    </div>
                                    <div className="ba-deal-sheet__body">
                                        <Spin spinning={dealLoading}>
                                            {currentDeal ? (
                                                <>
                                                    <Space wrap style={{marginBottom: 12}}>
                                                        {currentDeal.vacancy_name ? (
                                                            <Tag color="blue">
                                                                {currentDeal.vacancy_name}
                                                            </Tag>
                                                        ) : null}
                                                        {currentDeal.stage?.name ||
                                                        currentDeal.stage_name ? (
                                                            <Tag>
                                                                {currentDeal.stage?.name ||
                                                                    currentDeal.stage_name}
                                                            </Tag>
                                                        ) : null}
                                                        {currentDeal.contact_phone ? (
                                                            <Tag icon={<PhoneOutlined/>}>
                                                                {currentDeal.contact_phone}
                                                            </Tag>
                                                        ) : null}
                                                    </Space>

                                                    <Form
                                                        form={editForm}
                                                        layout="vertical"
                                                        requiredMark={false}
                                                        size="small"
                                                    >
                                                        <Form.Item
                                                            name="title"
                                                            label="Название"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'Укажите название'
                                                                }
                                                            ]}
                                                        >
                                                            <Input/>
                                                        </Form.Item>
                                                        <Form.Item name="city" label="Город">
                                                            <Input placeholder="Город кандидата"/>
                                                        </Form.Item>
                                                        <Form.Item name="contact_phone" label="Телефон">
                                                            <Input/>
                                                        </Form.Item>
                                                        <Form.Item name="stage_id" label="Стадия">
                                                            <Select
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={stageOptions.map(item => ({
                                                                    value: item.stage_id,
                                                                    label: item.stage_name
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="assigned_by_id"
                                                            label="Ответственный HR"
                                                        >
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={users.map(item => ({
                                                                    value: item.id,
                                                                    label: item.name
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item name="source_id" label="Источник">
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                options={sourceOptions.map(item => ({
                                                                    value: item.id,
                                                                    label: item.name
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                    </Form>

                                                    <div className="ba-deal-sheet__section">
                                                        <Space
                                                            style={{
                                                                width: '100%',
                                                                justifyContent: 'space-between',
                                                                marginBottom: 8
                                                            }}
                                                            wrap
                                                        >
                                                            <Title level={5} style={{margin: 0}}>
                                                                <PhoneOutlined/> Звонки
                                                                {dealCalls.length
                                                                    ? ` (${dealCalls.length})`
                                                                    : ''}
                                                            </Title>
                                                            <Button
                                                                size="small"
                                                                icon={<ReloadOutlined/>}
                                                                loading={dealCallsLoading}
                                                                onClick={() =>
                                                                    currentDeal?.id &&
                                                                    loadDealCalls(currentDeal.id, {
                                                                        refresh: true
                                                                    })
                                                                }
                                                            >
                                                                Обновить
                                                            </Button>
                                                        </Space>
                                                        {currentDeal.contact_phone ? (
                                                            <Text
                                                                type="secondary"
                                                                style={{display: 'block', marginBottom: 8}}
                                                            >
                                                                {currentDeal.contact_phone}
                                                            </Text>
                                                        ) : (
                                                            <Alert
                                                                type="info"
                                                                showIcon
                                                                style={{marginBottom: 8}}
                                                                message="Нет телефона — звонки недоступны"
                                                            />
                                                        )}
                                                        {dealCallsError ? (
                                                            <Alert
                                                                type="warning"
                                                                showIcon
                                                                message={dealCallsError}
                                                                style={{marginBottom: 8}}
                                                            />
                                                        ) : null}
                                                        {activeAudioName && audioUrls[activeAudioName] ? (
                                                            <div
                                                                style={{
                                                                    marginBottom: 10,
                                                                    padding: '8px 10px',
                                                                    background: '#fafafa',
                                                                    border: '1px solid #f0f0f0',
                                                                    borderRadius: 10
                                                                }}
                                                            >
                                                                <audio
                                                                    key={activeAudioName}
                                                                    controls
                                                                    autoPlay
                                                                    preload="metadata"
                                                                    src={audioUrls[activeAudioName]}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: 36
                                                                    }}
                                                                    onClick={event =>
                                                                        event.stopPropagation()
                                                                    }
                                                                />
                                                            </div>
                                                        ) : null}
                                                        <Spin spinning={dealCallsLoading}>
                                                            <Table
                                                                size="small"
                                                                rowKey={(row, idx) =>
                                                                    `${row.record_file_name || row.start || row.uuid || idx}`
                                                                }
                                                                dataSource={dealCalls}
                                                                pagination={false}
                                                                locale={{emptyText: 'Звонков нет'}}
                                                                scroll={{x: 420}}
                                                                columns={[
                                                                    {
                                                                        title: 'Дата',
                                                                        width: 120,
                                                                        render: (_, row) => {
                                                                            const raw =
                                                                                row.start ||
                                                                                row.start_time ||
                                                                                row.date ||
                                                                                row.call_date;
                                                                            return raw
                                                                                ? dayjs(raw).format(
                                                                                      'DD.MM HH:mm'
                                                                                  )
                                                                                : '—';
                                                                        }
                                                                    },
                                                                    {
                                                                        title: 'Сек.',
                                                                        width: 55,
                                                                        render: (_, row) =>
                                                                            toNumber(
                                                                                row.duration ||
                                                                                    row.conversation_duration ||
                                                                                    row.call_duration
                                                                            ) || '—'
                                                                    },
                                                                    {
                                                                        title: 'Запись',
                                                                        key: 'rec',
                                                                        render: (_, row) => {
                                                                            const filename =
                                                                                row.record_file_name;
                                                                            if (
                                                                                !filename &&
                                                                                !row.has_recording
                                                                            ) {
                                                                                return (
                                                                                    <Text type="secondary">
                                                                                        нет
                                                                                    </Text>
                                                                                );
                                                                            }
                                                                            if (!filename) return '—';
                                                                            const isActive =
                                                                                activeAudioName ===
                                                                                filename;
                                                                            const isLoading =
                                                                                audioLoadingName ===
                                                                                filename;
                                                                            return (
                                                                                <Button
                                                                                    size="small"
                                                                                    type={
                                                                                        isActive
                                                                                            ? 'primary'
                                                                                            : 'default'
                                                                                    }
                                                                                    loading={isLoading}
                                                                                    icon={
                                                                                        <SoundOutlined/>
                                                                                    }
                                                                                    onClick={async event => {
                                                                                        event.stopPropagation();
                                                                                        try {
                                                                                            await loadRecordingAudio(
                                                                                                filename
                                                                                            );
                                                                                        } catch (audioError) {
                                                                                            message.error(
                                                                                                audioError
                                                                                                    .response
                                                                                                    ?.data
                                                                                                    ?.error ||
                                                                                                    'Не удалось загрузить запись'
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {isActive
                                                                                        ? 'Играет'
                                                                                        : 'Слушать'}
                                                                                </Button>
                                                                            );
                                                                        }
                                                                    }
                                                                ]}
                                                            />
                                                        </Spin>
                                                    </div>

                                                    <div className="ba-deal-sheet__section">
                                                        <Title level={5} style={{marginBottom: 8}}>
                                                            Комментарии
                                                        </Title>
                                                        <div className="ba-deal-sheet__comment">
                                                            <TextArea
                                                                rows={2}
                                                                value={commentText}
                                                                placeholder="Новый комментарий…"
                                                                onChange={event =>
                                                                    setCommentText(event.target.value)
                                                                }
                                                            />
                                                            <Button
                                                                type="primary"
                                                                loading={commentSaving}
                                                                onClick={addComment}
                                                            >
                                                                Добавить
                                                            </Button>
                                                        </div>
                                                        {(currentDeal.comments || []).length ? (
                                                            <Timeline
                                                                style={{marginTop: 16}}
                                                                items={(currentDeal.comments || [])
                                                                    .slice(0, 20)
                                                                    .map((item, index) => ({
                                                                        key: item.id || index,
                                                                        children: (
                                                                            <div>
                                                                                <Text>
                                                                                    {item.text ||
                                                                                        item.comment ||
                                                                                        '—'}
                                                                                </Text>
                                                                                <div>
                                                                                    <Text type="secondary">
                                                                                        {item.author_name ||
                                                                                            item.user_name ||
                                                                                            'Автор'}
                                                                                        {' · '}
                                                                                        {formatDate(
                                                                                            item.created_at ||
                                                                                                item.createdAt
                                                                                        )}
                                                                                    </Text>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }))}
                                                            />
                                                        ) : (
                                                            <Text type="secondary">
                                                                Комментариев пока нет
                                                            </Text>
                                                        )}
                                                    </div>

                                                    <div className="ba-deal-sheet__section">
                                                        <Title level={5} style={{marginBottom: 8}}>
                                                            История стадий
                                                        </Title>
                                                        {(currentDeal.history || []).length ? (
                                                            <Timeline
                                                                items={(currentDeal.history || [])
                                                                    .slice(0, 15)
                                                                    .map((item, index) => ({
                                                                        key: item.id || index,
                                                                        color:
                                                                            (
                                                                                STAGE_META[
                                                                                    item.stage_name
                                                                                ] || {}
                                                                            ).color || '#1677ff',
                                                                        children: (
                                                                            <div>
                                                                                <Text strong>
                                                                                    {item.stage_name ||
                                                                                        item.stage_id ||
                                                                                        'Стадия'}
                                                                                </Text>
                                                                                <div>
                                                                                    <Text type="secondary">
                                                                                        {formatDate(
                                                                                            item.changed_at ||
                                                                                                item.created_at
                                                                                        )}
                                                                                    </Text>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }))}
                                                            />
                                                        ) : (
                                                            <Empty
                                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                                description="История пуста"
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <Empty description="Сделка не загружена"/>
                                            )}
                                        </Spin>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                <Drawer
                    title={
                        selectedVacancyId && vacancyDetail?.vacancy
                            ? vacancyDetail.vacancy.name
                            : 'Вакансии'
                    }
                    width={720}
                    open={vacancyPanelOpen}
                    onClose={() => {
                        setVacancyPanelOpen(false);
                        setSelectedVacancyId(null);
                        setVacancyDetail(null);
                    }}
                    destroyOnClose={false}
                    extra={
                        selectedVacancyId ? (
                            <Button
                                type="link"
                                onClick={() => {
                                    setSelectedVacancyId(null);
                                    setVacancyDetail(null);
                                    loadVacancyDirectory();
                                }}
                            >
                                К списку
                            </Button>
                        ) : (
                            <Space wrap>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined/>}
                                    onClick={() => {
                                        setVacancyCreateName('');
                                        setVacancyCreateActive(true);
                                        setVacancyCreateOpen(true);
                                    }}
                                >
                                    Создать
                                </Button>
                                <Button
                                    icon={<SyncOutlined/>}
                                    loading={vacancySyncing}
                                    onClick={syncVacancies}
                                >
                                    Синхронизировать из Битрикс
                                </Button>
                            </Space>
                        )
                    }
                >
                    {selectedVacancyId ? (
                        <Spin spinning={vacancyDetailLoading}>
                            {vacancyDetail ? (
                                <div className="ba-vacancy-detail">
                                    <div className="ba-stats__kpi ba-stats__kpi--wide">
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Всего</span>
                                            <span className="ba-stats__kpi-value">
                                                {toNumber(vacancyDetail.total)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Найм</span>
                                            <span
                                                className="ba-stats__kpi-value"
                                                style={{color: '#52c41a'}}
                                            >
                                                {toNumber(vacancyDetail.success)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Выход</span>
                                            <span
                                                className="ba-stats__kpi-value"
                                                style={{color: '#ff4d4f'}}
                                            >
                                                {toNumber(vacancyDetail.failed)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">% выхода</span>
                                            <span className="ba-stats__kpi-value">
                                                {toNumber(vacancyDetail.exit_rate)}%
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Конверсия</span>
                                            <span className="ba-stats__kpi-value">
                                                {toNumber(vacancyDetail.conversion)}%
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Звонки</span>
                                            <span className="ba-stats__kpi-value">
                                                {toNumber(vacancyDetail.calls_count)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Перемещения</span>
                                            <span className="ba-stats__kpi-value">
                                                {toNumber(vacancyDetail.movements_count)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Расходы</span>
                                            <span className="ba-stats__kpi-value" style={{fontSize: 18}}>
                                                {formatMoney(vacancyDetail.expenses_total || 0)}
                                            </span>
                                        </div>
                                        <div className="ba-stats__kpi-item">
                                            <span className="ba-stats__kpi-label">Ср. отклик</span>
                                            <span className="ba-stats__kpi-value" style={{fontSize: 18}}>
                                                {formatMoney(
                                                    vacancyDetail.avg_cost_per_response
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <Title level={5} style={{marginTop: 20}}>
                                        Конверсия по дням
                                    </Title>
                                    <Text
                                        type="secondary"
                                        style={{display: 'block', marginBottom: 8, fontSize: 12}}
                                    >
                                        Отклики по дате создания · расходы по дате расхода
                                    </Text>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="day"
                                        scroll={{x: 560}}
                                        dataSource={toArray(vacancyDetail.by_day)}
                                        locale={{emptyText: 'Нет данных по дням'}}
                                        columns={[
                                            {
                                                title: 'День',
                                                dataIndex: 'day',
                                                width: 120,
                                                render: day =>
                                                    day
                                                        ? dayjs(day).format('DD.MM.YYYY')
                                                        : '—'
                                            },
                                            {
                                                title: 'Отклики',
                                                dataIndex: 'responses',
                                                width: 90,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Найм',
                                                dataIndex: 'success',
                                                width: 70,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Конв. %',
                                                dataIndex: 'conversion',
                                                width: 80,
                                                align: 'right',
                                                render: v => `${toNumber(v)}%`
                                            },
                                            {
                                                title: 'Деньги',
                                                dataIndex: 'spend',
                                                width: 110,
                                                align: 'right',
                                                render: formatMoney
                                            },
                                            {
                                                title: 'Ср. стоимость отклика',
                                                dataIndex: 'avg_cost_per_response',
                                                align: 'right',
                                                render: formatMoney
                                            }
                                        ]}
                                    />

                                    <Title level={5} style={{marginTop: 20}}>
                                        По стадиям
                                    </Title>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey={row =>
                                            `${row.stage_id || row.stage_name}`
                                        }
                                        dataSource={toArray(vacancyDetail.by_stage)}
                                        columns={[
                                            {
                                                title: 'Стадия',
                                                dataIndex: 'stage_name',
                                                key: 'stage_name'
                                            },
                                            {
                                                title: 'Семантика',
                                                dataIndex: 'stage_semantic_id',
                                                key: 'sem',
                                                width: 100,
                                                render: sem =>
                                                    SEMANTIC_META[sem]?.name || sem
                                            },
                                            {
                                                title: 'Заявок',
                                                dataIndex: 'count',
                                                key: 'count',
                                                width: 90,
                                                align: 'right'
                                            }
                                        ]}
                                    />

                                    <Title level={5} style={{marginTop: 20}}>
                                        По HR
                                    </Title>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="assigned_by_id"
                                        dataSource={toArray(vacancyDetail.by_hr)}
                                        columns={[
                                            {
                                                title: 'HR',
                                                dataIndex: 'user_name',
                                                key: 'user_name'
                                            },
                                            {
                                                title: 'Всего',
                                                dataIndex: 'total',
                                                key: 'total',
                                                width: 70,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Найм',
                                                dataIndex: 'success',
                                                key: 'success',
                                                width: 70,
                                                align: 'right'
                                            },
                                            {
                                                title: 'Выход %',
                                                dataIndex: 'exit_rate',
                                                key: 'exit_rate',
                                                width: 80,
                                                align: 'right',
                                                render: v => `${v}%`
                                            }
                                        ]}
                                    />

                                    <Title level={5} style={{marginTop: 20}}>
                                        Расходы
                                    </Title>
                                    <Space
                                        wrap
                                        style={{marginBottom: 12, width: '100%'}}
                                        align="start"
                                    >
                                        <Input
                                            placeholder="Категория"
                                            value={expenseForm.category}
                                            onChange={e =>
                                                setExpenseForm(prev => ({
                                                    ...prev,
                                                    category: e.target.value
                                                }))
                                            }
                                            style={{width: 160}}
                                        />
                                        <InputNumber
                                            placeholder="Сумма"
                                            value={expenseForm.amount}
                                            onChange={value =>
                                                setExpenseForm(prev => ({
                                                    ...prev,
                                                    amount: value
                                                }))
                                            }
                                            min={0}
                                            style={{width: 120}}
                                        />
                                        <DatePicker
                                            value={
                                                expenseForm.expense_date
                                                    ? dayjs(expenseForm.expense_date)
                                                    : null
                                            }
                                            onChange={d =>
                                                setExpenseForm(prev => ({
                                                    ...prev,
                                                    expense_date: d
                                                        ? d.format('YYYY-MM-DD')
                                                        : null
                                                }))
                                            }
                                        />
                                        <Input
                                            placeholder="Комментарий"
                                            value={expenseForm.comment}
                                            onChange={e =>
                                                setExpenseForm(prev => ({
                                                    ...prev,
                                                    comment: e.target.value
                                                }))
                                            }
                                            style={{width: 180}}
                                        />
                                        <Button
                                            type="primary"
                                            loading={expenseSaving}
                                            onClick={saveExpense}
                                        >
                                            Добавить
                                        </Button>
                                    </Space>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="id"
                                        dataSource={vacancyExpenses}
                                        locale={{emptyText: 'Расходов пока нет'}}
                                        columns={[
                                            {
                                                title: 'Дата',
                                                dataIndex: 'expense_date',
                                                key: 'expense_date',
                                                width: 110,
                                                render: v => v || '—'
                                            },
                                            {
                                                title: 'Категория',
                                                dataIndex: 'category',
                                                key: 'category',
                                                render: v => v || '—'
                                            },
                                            {
                                                title: 'Сумма',
                                                dataIndex: 'amount',
                                                key: 'amount',
                                                width: 110,
                                                align: 'right',
                                                render: v =>
                                                    Number(v || 0).toLocaleString(
                                                        'ru-RU',
                                                        {maximumFractionDigits: 2}
                                                    )
                                            },
                                            {
                                                title: 'Комментарий',
                                                dataIndex: 'comment',
                                                key: 'comment',
                                                ellipsis: true,
                                                render: v => v || '—'
                                            },
                                            {
                                                title: '',
                                                key: 'actions',
                                                width: 70,
                                                render: (_, row) => (
                                                    <Popconfirm
                                                        title="Удалить расход?"
                                                        onConfirm={() =>
                                                            deleteExpense(row.id)
                                                        }
                                                    >
                                                        <Button type="link" danger size="small">
                                                            Удал.
                                                        </Button>
                                                    </Popconfirm>
                                                )
                                            }
                                        ]}
                                    />
                                </div>
                            ) : (
                                <Empty description="Нет данных"/>
                            )}
                        </Spin>
                    ) : (
                        <Spin spinning={vacancyDirectoryLoading}>
                            <Table
                                size="small"
                                rowKey="id"
                                pagination={false}
                                dataSource={vacancyDirectory}
                                onRow={record => ({
                                    onClick: () => loadVacancyDetail(record.id),
                                    style: {cursor: 'pointer'}
                                })}
                                columns={[
                                    {
                                        title: 'Вакансия',
                                        dataIndex: 'name',
                                        key: 'name',
                                        render: (name, row) => (
                                            <Space direction="vertical" size={0}>
                                                <Text strong>{name}</Text>
                                                <Text type="secondary" style={{fontSize: 11}}>
                                                    enum {row.bitrix_enum_id || '—'}
                                                    {row.is_stale ? ' · устарела' : ''}
                                                </Text>
                                            </Space>
                                        )
                                    },
                                    {
                                        title: 'Всего',
                                        dataIndex: 'total',
                                        key: 'total',
                                        width: 70,
                                        align: 'right',
                                        render: v => toNumber(v)
                                    },
                                    {
                                        title: 'Найм',
                                        dataIndex: 'success',
                                        key: 'success',
                                        width: 70,
                                        align: 'right',
                                        render: v => toNumber(v)
                                    },
                                    {
                                        title: 'Выход',
                                        dataIndex: 'failed',
                                        key: 'failed',
                                        width: 70,
                                        align: 'right',
                                        render: v => toNumber(v)
                                    },
                                    {
                                        title: 'Активна',
                                        dataIndex: 'is_active',
                                        key: 'is_active',
                                        width: 90,
                                        render: (active, row) => (
                                            <span
                                                onClick={event =>
                                                    event.stopPropagation()
                                                }
                                            >
                                                <Switch
                                                    checked={Boolean(active)}
                                                    onChange={checked =>
                                                        toggleVacancyActive(
                                                            row.id,
                                                            checked
                                                        )
                                                    }
                                                />
                                            </span>
                                        )
                                    }
                                ]}
                                locale={{
                                    emptyText:
                                        'Справочник пуст — создайте вакансию или синхронизируйте из Битрикс'
                                }}
                            />
                        </Spin>
                    )}
                </Drawer>

                <Modal
                    title="Новая вакансия"
                    open={vacancyCreateOpen}
                    onCancel={() => setVacancyCreateOpen(false)}
                    onOk={createVacancy}
                    confirmLoading={vacancyCreating}
                    okText="Создать"
                    cancelText="Отмена"
                    destroyOnClose
                >
                    <div className="ba-filter-field" style={{marginBottom: 12}}>
                        <span className="ba-filter-label">Название</span>
                        <Input
                            autoFocus
                            placeholder="Например: Менеджер по продажам"
                            value={vacancyCreateName}
                            onChange={event =>
                                setVacancyCreateName(event.target.value)
                            }
                            onPressEnter={createVacancy}
                        />
                    </div>
                    <Space>
                        <Switch
                            checked={vacancyCreateActive}
                            onChange={setVacancyCreateActive}
                        />
                        <Text>Активна сразу</Text>
                    </Space>
                    <Text
                        type="secondary"
                        style={{display: 'block', marginTop: 12, fontSize: 12}}
                    >
                        Локальная вакансия в CRM. Для появления в enum Битрикс
                        синхронизируйте или заведите поле там отдельно.
                    </Text>
                </Modal>

                <Modal
                    title="Сохранить фильтр"
                    open={presetModalOpen}
                    onCancel={() => setPresetModalOpen(false)}
                    onOk={saveFilterPreset}
                    confirmLoading={presetSaving}
                    okText="Сохранить"
                    cancelText="Отмена"
                    destroyOnClose
                >
                    <div className="ba-filter-field" style={{marginBottom: 12}}>
                        <span className="ba-filter-label">Название</span>
                        <Input
                            autoFocus
                            placeholder="Например: Месяц · все вакансии"
                            value={presetName}
                            onChange={event => setPresetName(event.target.value)}
                            onPressEnter={saveFilterPreset}
                        />
                    </div>
                    {analyticsScope.can_save_global_filters ? (
                        <Checkbox
                            checked={presetGlobal}
                            onChange={event =>
                                setPresetGlobal(event.target.checked)
                            }
                        >
                            Сохранить для всех пользователей (глобально)
                        </Checkbox>
                    ) : (
                        <Text type="secondary">
                            {isSelfOnlyHr
                                ? 'Фильтр сохранится только для вас и только по вашим сделкам'
                                : 'Фильтр сохранится только для вас'}
                        </Text>
                    )}
                </Modal>
            </Layout>
        </ConfigProvider>
    );
};


export default BitrixAnalyticsPage;
